import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiBook,
  FiMic,
  FiFileText,
  FiUsers,
  FiMessageCircle,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiCheck,
  FiTarget,
  FiHome,
  FiBookOpen,
  FiVideo,
  FiArrowRight,
  FiSun,
  FiSunrise,
  FiClipboard,
  FiMinus,
} from 'react-icons/fi';
import ProfilePicture from '../../components/ProfilePicture';
import Sidebar from '../Sidebar';
import {
  API_BASE_URL,
  getAuthHeaders,
} from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import {
  activityDurationMinutes,
  formatIeltsActivityTitle,
  formatIeltsActivitySublabel,
  formatIeltsHomeTask,
  getCurrentJourneyDay,
} from '../../utils/languageJourneyData';
import { loadJourneyHoldIntervals } from '../../utils/progressHold';
import EnglishRewardCard from '../exams/english/EnglishRewardCard';
import ToeflNextStepCard from '../exams/toefl/ToeflNextStepCard';
import { readStoredLanguageJourney } from '../exams/toefl/toeflOffer';
import { fetchEnglishRewardEligibility } from '../../utils/englishRewardApi';
import {
  fetchToeflBootcamp,
  fetchToeflDailyContent,
} from '../exams/toefl/api';
import {
  getPremiumLandingPath,
  isGreOnlySubscriber,
  isIeltsOnlySubscriber,
} from '../../utils/subscriptionProductScope';
import ProgramTimeline from '../../components/ProgramTimeline';
import GraduateTargeting from './GraduateTargeting';
import { buildApplicationHomeTasks } from './applicationHomeTasks';
import {
  countNewCommunityQuestions,
  normalizeCommunityPosts,
} from './communityHomeStats';
import { fetchGreTestResultsRaw } from '../exams/gre/api/greTestsAPI';
import {
  readGreCompletionDateFromLocalCache,
  readGreDetailedResultFromLocalCache,
} from '../exams/gre/api/greResultCache';
import {
  getEffectiveQuantWeekNumber,
  getQuantWeekendModule,
  getSundayDifficultyMix,
  getSundayPracticeStageLabel,
  QUANT_WEEKEND_MODULES,
} from '../exams/gre/quantWeekendPlan';
import { getQuantTrack, getQuantTrackLabel } from '../exams/gre/quantProfileUtils';
import ProgressHoldBanner from '../../components/ProgressHoldBanner';
import {
  fetchDailyArticlesForHome,
  loadHomeProgramWindows,
  articlesFromProgramWindow,
  dateForHomeProgramOffset,
  greVocabHomeTasksFromArticles,
  mergeHomeProgramWindows,
  resolveSelectedJourneyDay,
  fetchHomeTodayProgress,
  frozenGreExerciseTarget,
  getDailyVocabTaskProgress,
  getTaskItemClassName,
  markHomeTaskEvent,
  resolveHomeTaskStatus,
  getLocalDateKey,
} from './homeTaskStatus';
import {
  HOME_SNAPSHOT_TTLS_MS,
  isHomeSliceFresh,
  readHomeSnapshot,
  writeHomeSnapshotSlice,
} from './homeSnapshot';
import {
  fetchIeltsProfile,
  isIeltsProfileAssessed,
} from '../../utils/ieltsProfile';
import './Home.css';

const GRE_VERBAL_EXERCISE_MILESTONES = [
  { words: 0, exercises: 0 },
  { words: 10, exercises: 10 },
  { words: 25, exercises: 15 },
  { words: 50, exercises: 20 },
  { words: 75, exercises: 25 },
  { words: 100, exercises: 30 },
  { words: 150, exercises: 35 },
  { words: 200, exercises: 40 },
  { words: 300, exercises: 45 },
  { words: 400, exercises: 50 },
  { words: 500, exercises: 55 },
  { words: 650, exercises: 60 },
  { words: 800, exercises: 65 },
  { words: 1000, exercises: 70 },
  { words: 1200, exercises: 75 },
];

const getUnlockedGreVerbalExerciseCount = (masteredWords) => {
  if (masteredWords === null || masteredWords === undefined) {
    return null;
  }

  const safeMasteredWords = Math.max(0, Number(masteredWords) || 0);
  const currentMilestone =
    GRE_VERBAL_EXERCISE_MILESTONES.slice()
      .reverse()
      .find((milestone) => safeMasteredWords >= milestone.words) ||
    GRE_VERBAL_EXERCISE_MILESTONES[0];

  return currentMilestone.exercises > 0
    ? currentMilestone.exercises
    : safeMasteredWords > 0
      ? 5
      : 0;
};

const formatGreExerciseTaskLabel = (label, count) =>
  typeof count === 'number' ? `${label} (${count})` : label;

const HOME_SUMMARY_STORAGE_KEY = 'liwaza_home_summary';

const formatHomeSummarySessionDate = (bookingDate) => {
  if (!bookingDate) return null;
  const parsedDate = new Date(bookingDate);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatHomeSummarySessionTime = (startTime) => {
  if (!startTime) return null;
  const parsedTime = new Date(`1970-01-01T${startTime}:00`);
  if (Number.isNaN(parsedTime.getTime())) return startTime;
  return parsedTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatHomeSummaryDateTime = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return {
    date: parsedDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }),
    time: parsedDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
  };
};

const normalizeCommunityHighlightTitle = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const escapeIcsText = (value) =>
  String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const formatIcsDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
};

const downloadCalendarInvite = ({
  title,
  startDate,
  durationMinutes = 60,
  description,
  path,
}) => {
  if (typeof window === 'undefined') return false;

  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return false;

  const end = new Date(
    start.getTime() + Math.max(Number(durationMinutes) || 60, 15) * 60 * 1000
  );
  const startStamp = formatIcsDate(start);
  const endStamp = formatIcsDate(end);
  if (!startStamp || !endStamp) return false;

  const absoluteUrl = path ? `${window.location.origin}${path}` : '';
  const calendarFile = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Liwaza//Home Highlights//EN',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@liwaza.com`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${startStamp}`,
    `DTEND:${endStamp}`,
    `SUMMARY:${escapeIcsText(title || 'Liwaza Event')}`,
    `DESCRIPTION:${escapeIcsText(description || absoluteUrl)}`,
    absoluteUrl ? `URL:${escapeIcsText(absoluteUrl)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');

  const blob = new Blob([calendarFile], {
    type: 'text/calendar;charset=utf-8',
  });
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${String(title || 'liwaza-event')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'liwaza-event'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return true;
};

/** GRE Verbal/Quant scaled scores cap at 170; never show a "next" target below current once the plan score is met. */
const greSectionDisplayTarget = (current, planTarget) => {
  const c = Number(current) || 0;
  const t = Number(planTarget) || 0;
  if (c <= 0) return t;
  if (c < t) return t;
  return Math.min(c + 5, 170);
};

/** Prefer vocabulary enrollment total length (user-adjusted) over training plan's initial `duration_weeks`. */
const getEffectiveGrePlanWeeks = (plan) => {
  try {
    const raw = localStorage.getItem('gre_vocabulary_enrollment');
    if (raw) {
      const e = JSON.parse(raw);
      const enrolled = e.isEnrolled === true || e.is_enrolled === true;
      if (enrolled) {
        const days = e.durationDays ?? e.duration_days;
        if (days != null && Number(days) > 0) {
          return Math.max(1, Math.round(Number(days) / 7));
        }
      }
    }
  } catch {
    // ignore
  }
  return plan?.duration_weeks || 12;
};

const Home = () => {
  const navigate = useNavigate();
  const [user] = useState(() => getStoredUser());

  const greOnlyUser = Boolean(user?.is_premium && isGreOnlySubscriber(user));
  const ieltsOnlyUser = Boolean(
    user?.is_premium && isIeltsOnlySubscriber(user)
  );

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showMoreTasks, setShowMoreTasks] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState({});
  const [dailyArticles, setDailyArticles] = useState([]);
  const [greWindowDays, setGreWindowDays] = useState([]);
  const [journeyWindowDays, setJourneyWindowDays] = useState([]);
  const [selectedProgramOffset, setSelectedProgramOffset] = useState(() => {
    try {
      const stored =
        sessionStorage.getItem('home_program_offset') ||
        sessionStorage.getItem('gre_program_offset');
      const offset = Number(stored);
      return [-1, 0, 1, 2].includes(offset) ? offset : 0;
    } catch {
      return 0;
    }
  });
  const programWindowDays = useMemo(
    () => mergeHomeProgramWindows(greWindowDays, journeyWindowDays),
    [greWindowDays, journeyWindowDays]
  );
  const [todayProgress, setTodayProgress] = useState(null);
  const [unlockedGreCount, setUnlockedGreCount] = useState(null);
  const [showAllNotifs, setShowAllNotifs] = useState(false);

  // Real data states
  const [greData, setGreData] = useState(null);
  const [ieltsData, setIeltsData] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [coachingData, setCoachingData] = useState(null);
  const [communityData, setCommunityData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Dynamic tasks based on day of week
  const [todayTasksData, setTodayTasksData] = useState({
    gre: [],
    ielts: [],
    priority: [],
  });
  const [moreTasksData, setMoreTasksData] = useState({
    mbaApp: [],
    weekend: [],
  });
  const [verbalProgress, setVerbalProgress] = useState(null);
  const [admissionsHome, setAdmissionsHome] = useState(null);
  const [applicationWorkspace, setApplicationWorkspace] = useState({
    completion: { story: false, cv: false, essay: false, lor: false },
  });

  // Article boost status
  const [boostStatus, setBoostStatus] = useState(null);
  const [englishRewardEligibility, setEnglishRewardEligibility] = useState(null);
  const [toeflBootcamp, setToeflBootcamp] = useState(null);

  // Training Plan real data
  const [realTrainingPlan, setRealTrainingPlan] = useState(null);

  // Mandatory onboarding tasks (GRE Diagnostic + IELTS Assessment)
  const [mandatoryTasks, setMandatoryTasks] = useState({
    greDiagnostic: { completed: false, path: '/exams/gre/full/full-test-1' },
    ieltsAssessment: { completed: false, path: '/exams/english/speaking' },
  });

  // Date override for testing (can be set via window.setTestDate)
  const [dateOverride, setDateOverride] = useState(null);

  // Expose date override function to window for testing
  useEffect(() => {
    window.setTestDate = (dateString) => {
      if (dateString === null) {
        setDateOverride(null);
        setCurrentTime(new Date());
      } else {
        const newDate = new Date(dateString);
        setDateOverride(newDate);
        setCurrentTime(newDate);
      }
    };
    return () => {
      delete window.setTestDate;
    };
  }, []);

  // Update time every minute (respecting date override)
  useEffect(() => {
    if (!dateOverride) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 60000);
      return () => clearInterval(timer);
    }
  }, [dateOverride]);

  // ========== DATA FETCHING FUNCTIONS ==========

  // 1. Fetch GRE data - prioritize latest test scores, fall back to diagnostic scores
  const fetchGreData = useCallback(() => {
    try {
      const trainingPlan = localStorage.getItem('liwaza-training-plan');
      const plan = trainingPlan ? JSON.parse(trainingPlan) : null;

      // Get target scores from training plan - support both nested and flat structures
      const targetVerbal =
        plan?.target_scores?.verbal || plan?.target_verbal || 160;
      const targetQuant =
        plan?.target_scores?.quantitative || plan?.target_quant || 162;
      const targetAwa = plan?.target_scores?.awa || plan?.target_awa || 4.0;
      const targetScore =
        plan?.target_score || targetVerbal + targetQuant || 322;
      const planWeeks = getEffectiveGrePlanWeeks(plan);

      // Get diagnostic/initial scores from scoreAnalysis or flat structure
      const diagnosticVerbal =
        plan?.scoreAnalysis?.verbalScore || plan?.diagnostic_verbal || 130;
      const diagnosticQuant =
        plan?.scoreAnalysis?.quantScore || plan?.diagnostic_quant || 130;
      const diagnosticAwa =
        plan?.scoreAnalysis?.awaScore || plan?.diagnostic_awa || 2.0;

      // Try to get the latest completed test scores
      const testIds = [
        'full-test-1',
        'full-test-2',
        'full-test-3',
        'full-test-4',
        'full-test-5',
        'full-test-6',
        'full-test-7',
        'full-test-8',
        'full-test-9',
        'full-test-10',
      ];

      let latestScores = null;
      let latestDate = 0;

      // Check localStorage for completed test results
      testIds.forEach((testId) => {
        const results = readGreDetailedResultFromLocalCache(user?.email, testId);
        if (results) {
          const completionDate = results.completionDate || results.created_at;
          if (
            completionDate &&
            new Date(completionDate).getTime() > latestDate
          ) {
            latestDate = new Date(completionDate).getTime();
            latestScores = results;
          }
        }
      });

      // Use latest test scores if available, otherwise fall back to diagnostic scores
      if (latestScores) {
        // Support both flat structure and enhancedScores nested structure
        const verbal =
          latestScores.enhancedScores?.verbal?.enhanced ||
          latestScores.verbal ||
          latestScores.verbalScore ||
          0;
        const quant =
          latestScores.enhancedScores?.quant?.enhanced ||
          latestScores.quant ||
          latestScores.quantScore ||
          0;
        const awa =
          latestScores.enhancedScores?.awa?.score ||
          latestScores.awa ||
          latestScores.awaScore ||
          0;
        setGreData({
          currentScore: verbal + quant,
          maxScore: 340,
          verbal: verbal,
          quant: quant,
          awa: awa,
          targetScore: targetScore,
          targetVerbal: greSectionDisplayTarget(verbal, targetVerbal),
          targetQuant: greSectionDisplayTarget(quant, targetQuant),
          targetAwa: targetAwa,
          planWeeks: planWeeks,
        });
      } else if (plan) {
        // Fall back to diagnostic scores from training plan
        setGreData({
          currentScore: diagnosticVerbal + diagnosticQuant,
          maxScore: 340,
          verbal: diagnosticVerbal,
          quant: diagnosticQuant,
          awa: diagnosticAwa,
          targetScore: targetScore,
          targetVerbal: greSectionDisplayTarget(diagnosticVerbal, targetVerbal),
          targetQuant: greSectionDisplayTarget(diagnosticQuant, targetQuant),
          targetAwa: targetAwa,
          planWeeks: planWeeks,
        });
      } else {
        // Default values if no plan and no test results exist
        setGreData({
          currentScore: 285,
          maxScore: 340,
          verbal: 142,
          quant: 143,
          awa: 3.0,
          targetScore: 320,
          targetVerbal: greSectionDisplayTarget(142, 160),
          targetQuant: greSectionDisplayTarget(143, 160),
          targetAwa: 4.0,
          planWeeks: 12,
        });
      }
    } catch (error) {
      console.error('Error fetching GRE data:', error);
    }
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return undefined;
    const cached = readHomeSnapshot(user.email)?.slices?.englishReward;
    if (isHomeSliceFresh(cached, HOME_SNAPSHOT_TTLS_MS.englishReward)) {
      setEnglishRewardEligibility(cached.data);
      return undefined;
    }
    let cancelled = false;
    fetchEnglishRewardEligibility()
      .then((payload) => {
        if (cancelled) return;
        setEnglishRewardEligibility(payload);
        writeHomeSnapshotSlice(user.email, 'englishReward', payload);
      })
      .catch(() => {
        if (!cancelled) setEnglishRewardEligibility(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  // 2. Fetch IELTS data from localStorage (prioritize languageJourney)
  const fetchIeltsData = useCallback(() => {
    try {
      const languageJourney = localStorage.getItem(
        `languageJourney_${user?.email}`
      );

      if (languageJourney) {
        const journey = JSON.parse(languageJourney);
        if (journey?.enrolled) {
          // Calculate progress based on completed days
          const totalDays = journey.journey?.length || 60;
          const completedDays =
            journey.journey?.filter(
              (d) => d.status === 'completed' || d.status === 'partial'
            ).length || 0;
          const progressPercent = Math.round((completedDays / totalDays) * 100);

          setIeltsData({
            currentLevel: journey.currentLevel || 'B1',
            targetLevel: journey.targetLevel || 'B2',
            progress: progressPercent,
            speaking: journey.skills?.speaking || journey.currentLevel || 'B1',
            listening:
              journey.skills?.listening || journey.currentLevel || 'B1',
            reading: journey.skills?.reading || journey.currentLevel || 'B1',
            writing: journey.skills?.writing || journey.currentLevel || 'A2',
          });
          return;
        }
      }

      // Default values if no enrolled journey
      setIeltsData({
        currentLevel: 'B1',
        targetLevel: 'B2',
        progress: 0,
        speaking: 'B1',
        listening: 'B1',
        reading: 'B1',
        writing: 'A2',
      });
    } catch (error) {
      console.error('Error fetching IELTS data:', error);
    }
  }, [user?.email]);

  const deriveDocumentCompletion = (applications) => {
    const completion = { story: false, cv: false, essay: false, lor: false };
    (applications || []).forEach((application) => {
      (application.documents || []).forEach((document) => {
        if (
          Object.prototype.hasOwnProperty.call(completion, document.kind) &&
          String(document.body || '').trim()
        ) {
          completion[document.kind] = true;
        }
      });
    });
    return completion;
  };

  const deriveDeadlinesFromApplications = (applications, now) => {
    const datePattern = /\b(20\d{2}-\d{2}-\d{2})\b/;
    const deadlinesData = [];
    const seen = new Set();
    (applications || []).forEach((application) => {
      const schoolName = application.program?.school_name;
      if (!schoolName) return;
      (application.program?.requirements || []).forEach((req) => {
        const label = req.label || '';
        const value = req.value || '';
        const haystack = `${req.kind || ''} ${label} ${value}`.toLowerCase();
        if (!haystack.includes('deadline')) return;
        const match = String(value).match(datePattern) || String(label).match(datePattern);
        if (!match) return;
        const deadlineDate = new Date(match[1]);
        if (Number.isNaN(deadlineDate.getTime()) || deadlineDate <= now) return;
        const key = `${schoolName}-${match[1]}`;
        if (seen.has(key)) return;
        seen.add(key);
        const daysLeft = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
        deadlinesData.push({
          school: schoolName,
          program: application.program?.name,
          round: label || 'Deadline',
          daysLeft,
          priority: daysLeft < 30 ? 'high' : daysLeft < 60 ? 'medium' : 'low',
        });
      });
    });
    return deadlinesData.sort((a, b) => a.daysLeft - b.daysLeft).slice(0, 5);
  };

  // 3. Fetch application deadlines and document completion from the API
  const fetchDeadlines = useCallback(async () => {
    try {
      const [homeRes, appsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admissions/home`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE_URL}/api/applications`, {
          headers: getAuthHeaders(),
        }),
      ]);
      const home = homeRes.ok ? await homeRes.json() : null;
      const appsPayload = appsRes.ok ? await appsRes.json() : null;
      if (!home && !appsRes.ok) return;
      if (home) {
        setAdmissionsHome(home);
      }
      const applications = appsPayload?.applications || [];
      const completion =
        home?.document_completion || deriveDocumentCompletion(applications);
      setApplicationWorkspace({ applications, completion });
      const now = dateOverride || new Date();
      const apiDeadlines = Array.isArray(home?.deadlines) ? home.deadlines : [];
      const nextDeadlines = apiDeadlines.length
        ? apiDeadlines.slice(0, 5)
        : deriveDeadlinesFromApplications(applications, now);
      setDeadlines(nextDeadlines);
      if (user?.email && (home || appsRes.ok)) {
        writeHomeSnapshotSlice(user.email, 'admissions', {
          home,
          workspace: { applications, completion },
          deadlines: nextDeadlines,
        });
      }
    } catch (error) {
      console.error('Error fetching deadlines:', error);
    }
  }, [dateOverride, user?.email]);

  // 4. Read coaching/community summary from localStorage config bootstrap
  const readHomeSummaryFromLocalStorage = useCallback(() => {
    if (!user?.email) {
      setCoachingData(null);
      setBoostStatus(null);
      setCommunityData({
        newQuestions: 0,
        newReplies: 0,
        upcomingEvent: null,
        upcomingWebinar: null,
      });
      return;
    }

    try {
      const rawSummary = localStorage.getItem(HOME_SUMMARY_STORAGE_KEY);
      if (!rawSummary) {
        setCoachingData(null);
        setBoostStatus(null);
        setCommunityData({
          newQuestions: 0,
          newReplies: 0,
          upcomingEvent: null,
          upcomingWebinar: null,
        });
        return;
      }

      const parsedSummary = JSON.parse(rawSummary);
      const nextSession = parsedSummary?.coaching?.nextSession;
      const summaryBoostStatus = parsedSummary?.gre?.boostStatus;

      if (nextSession) {
        setCoachingData({
          nextSession: {
            date: formatHomeSummarySessionDate(nextSession.bookingDate),
            time: formatHomeSummarySessionTime(nextSession.startTime),
            coach: nextSession.coach || 'Coach',
            topic: nextSession.topic || 'Session',
          },
        });
      } else {
        setCoachingData(null);
      }

      setBoostStatus(
        summaryBoostStatus && typeof summaryBoostStatus === 'object'
          ? summaryBoostStatus
          : null
      );

      const upcomingEvent = parsedSummary?.community?.upcomingEvent;
      const upcomingWebinar = parsedSummary?.community?.upcomingWebinar;
      const formattedEventDateTime = formatHomeSummaryDateTime(upcomingEvent?.date);
      const formattedWebinarDateTime = formatHomeSummaryDateTime(
        upcomingWebinar?.date
      );

      setCommunityData({
        newQuestions: Number(parsedSummary?.community?.newQuestions) || 0,
        newReplies: Number(parsedSummary?.community?.newReplies) || 0,
        upcomingEvent: upcomingEvent
          ? {
              ...upcomingEvent,
              dateLabel: formattedEventDateTime?.date || null,
              timeLabel: formattedEventDateTime?.time || null,
            }
          : null,
        upcomingWebinar: upcomingWebinar
          ? {
              ...upcomingWebinar,
              dateLabel: formattedWebinarDateTime?.date || null,
              timeLabel: formattedWebinarDateTime?.time || null,
            }
          : null,
      });
    } catch (error) {
      console.error('Error reading home summary from localStorage:', error);
      setCoachingData(null);
      setBoostStatus(null);
      setCommunityData({
        newQuestions: 0,
        newReplies: 0,
        upcomingEvent: null,
        upcomingWebinar: null,
      });
    }
  }, [user?.email]);

  const fetchCommunityQuestionStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/posts?page=1&limit=20`);
      if (!response.ok) return;
      const payload = await response.json();
      const posts = normalizeCommunityPosts(payload);
      const newQuestions = countNewCommunityQuestions(posts);
      setCommunityData((prev) => ({
        ...(prev || {
          newQuestions: 0,
          newReplies: 0,
          upcomingEvent: null,
          upcomingWebinar: null,
        }),
        newQuestions,
      }));
      if (user?.email) {
        writeHomeSnapshotSlice(user.email, 'community', { newQuestions });
      }
    } catch (error) {
      console.error('Error fetching community questions:', error);
    }
  }, [user?.email]);

  // ========== DYNAMIC TASKS HELPERS ==========

  // Get week number in 2-week cycle (for determining when to show GRE mock test)
  const getWeekNumberInCycle = useCallback((forDate) => {
    const trainingPlan = localStorage.getItem('liwaza-training-plan');
    if (!trainingPlan) return 1;

    try {
      const plan = JSON.parse(trainingPlan);
      if (!plan?.generatedAt) return 1;

      const effectiveDate = forDate || dateOverride || currentTime;
      const startDate = new Date(plan.generatedAt);
      const daysSinceStart = Math.floor(
        (effectiveDate - startDate) / (1000 * 60 * 60 * 24)
      );
      const weeksSinceStart = Math.floor(daysSinceStart / 7);

      return (weeksSinceStart % 2) + 1; // Returns 1 or 2
    } catch {
      return 1;
    }
  }, [dateOverride, currentTime]);

  // Get articles per day based on training plan duration and boost status
  const getArticlesPerDay = useCallback(() => {
    const trainingPlan = localStorage.getItem('liwaza-training-plan');
    if (!trainingPlan) return 3;

    try {
      const plan = JSON.parse(trainingPlan);
      const weeks = getEffectiveGrePlanWeeks(plan);

      // Base articles per day based on plan duration
      let baseArticles;
      if (weeks >= 20)
        baseArticles = 2; // 5 months
      else if (weeks >= 16)
        baseArticles = 2; // 4 months
      else if (weeks >= 12)
        baseArticles = 3; // 3 months
      else if (weeks >= 8)
        baseArticles = 3; // 2 months
      else baseArticles = 4; // 1 month

      // Apply boost multiplier if active (only for 4+ month plans)
      const months = Math.ceil(weeks / 4);
      if (
        boostStatus?.has_active_boost &&
        (months === 4 || months === 5) &&
        boostStatus.boost_multiplier
      ) {
        return Math.min(baseArticles * boostStatus.boost_multiplier, 5); // Cap at 5
      }

      return baseArticles;
    } catch {
      return 3;
    }
  }, [boostStatus]);

  const getCompletedFullTestsFromLocalStorage = useCallback(() => {
    const completed = [];
    const testIds = [
      'full-test-1',
      'full-test-2',
      'full-test-3',
      'full-test-4',
      'full-test-5',
      'full-test-6',
      'full-test-7',
      'full-test-8',
      'full-test-9',
      'full-test-10',
    ];
    testIds.forEach((testId) => {
      const completionDate = readGreCompletionDateFromLocalCache(
        user?.email,
        testId
      );
      if (completionDate) {
        completed.push({ id: testId, completionDate });
        return;
      }

      const parsed = readGreDetailedResultFromLocalCache(user?.email, testId);
      if (parsed) {
        const parsedDate = parsed?.completionDate || parsed?.created_at;
        completed.push({
          id: testId,
          completionDate: parsedDate || null,
        });
      }
    });

    return completed;
  }, [user?.email]);

  const getLatestCompletedFullTestNumber = useCallback((completedTestIds) => {
    const numbers = completedTestIds
      .map((testId) => {
        const match = testId.match(/full-test-(\d+)/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((value) => Number.isInteger(value))
      .sort((a, b) => a - b);

    return numbers.length > 0 ? numbers[numbers.length - 1] : 0;
  }, []);

  // 6. Build real training plan data from localStorage
  const buildRealTrainingPlan = useCallback(() => {
    const trainingPlanData = localStorage.getItem('liwaza-training-plan');
    if (!trainingPlanData) return null;

    try {
      const plan = JSON.parse(trainingPlanData);
      const articlesPerDay = getArticlesPerDay();
      const wordsPerDay = articlesPerDay * 6; // Words = Articles × 6
      const planStartDate = plan?.generatedAt
        ? new Date(plan.generatedAt)
        : currentTime;
      const daysSincePlanStart = Math.max(
        0,
        Math.floor((currentTime - planStartDate) / (1000 * 60 * 60 * 24))
      );
      const quantTrack = getQuantTrack();
      const quantWeekNumber = getEffectiveQuantWeekNumber(
        Math.floor(daysSincePlanStart / 7) + 1,
        quantTrack
      );
      const quantModule = getQuantWeekendModule(quantWeekNumber);
      const quantSundayPractice = getSundayPracticeStageLabel(quantWeekNumber);
      const quantDifficultyMix = getSundayDifficultyMix(quantWeekNumber);

      // Calculate next mock test based on 2-week cycle (use currentTime for testing)
      const now = currentTime;
      const milestones = plan?.milestones || [];
      const completedMilestoneIds = milestones
        .filter((m) => m.milestone_status === 'completed')
        .map((m) => m.test_id)
        .filter(Boolean);
      const completedLocalTests = getCompletedFullTestsFromLocalStorage();
      const completedLocalIds = completedLocalTests.map((test) => test.id);
      const completedTestIds = Array.from(
        new Set([...completedMilestoneIds, ...completedLocalIds])
      );
      const completedTests = completedTestIds.length;
      const totalTests = plan?.milestones?.length || 10;

      // Find next test date based on 2-week cycle
      const getNextTestDate = () => {
        const startDate = plan?.generatedAt ? new Date(plan.generatedAt) : now;
        const daysSinceStart = Math.floor(
          (now - startDate) / (1000 * 60 * 60 * 24)
        );
        const weeksSinceStart = Math.floor(daysSinceStart / 7);
        const dayOfWeek = now.getDay(); // 0=Sunday, 6=Saturday
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const isSecondWeekendOfCycle = weeksSinceStart % 2 === 1; // Week 2 of cycle

        // If today is a 2nd weekend, the test is TODAY
        if (isWeekend && isSecondWeekendOfCycle) {
          return now;
        }

        // Otherwise, find next Saturday of a 2nd weekend
        const weeksUntilNextTest = isSecondWeekendOfCycle
          ? 2
          : 2 - (weeksSinceStart % 2);
        const daysUntilNextWeek = weeksUntilNextTest * 7 - (daysSinceStart % 7);

        // Find the next Saturday (day 6)
        const targetDate = new Date(
          now.getTime() + daysUntilNextWeek * 24 * 60 * 60 * 1000
        );
        const daysToSaturday = (6 - targetDate.getDay() + 7) % 7;
        targetDate.setDate(targetDate.getDate() + daysToSaturday);

        return targetDate;
      };

      const nextTestDate = getNextTestDate();
      const latestCompletedNumber =
        getLatestCompletedFullTestNumber(completedTestIds);
      const nextTestNumber = Math.min(totalTests, latestCompletedNumber + 1);
      const nextTestId = `full-test-${nextTestNumber}`;
      const nextTestLabel = `Full Mock Test #${nextTestNumber}`;

      // Calculate target date based on test 1 completion (same logic as GRE.js)
      const getTargetDateForTest = (testNumber) => {
        const test1CompletionDate = readGreCompletionDateFromLocalCache(
          user?.email,
          'full-test-1'
        );
        if (test1CompletionDate && testNumber > 1) {
          const test1Date = new Date(test1CompletionDate);
          const weeksAfterTest1 = (testNumber - 1) * 2;
          const targetDate = new Date(test1Date);
          targetDate.setDate(targetDate.getDate() + weeksAfterTest1 * 7);
          return targetDate;
        }
        return null;
      };

      const targetDate = getTargetDateForTest(nextTestNumber);
      const isOverdue =
        targetDate && now > targetDate && nextTestNumber > 1
          ? Math.floor((now - targetDate) / (1000 * 60 * 60 * 24))
          : 0;

      // Build tests list with past, current, and future tests
      const allTestsDone = completedTests >= totalTests;

      // Get past tests (completed)
      const sortedCompletedNumbers = completedTestIds
        .map((testId) => {
          const match = testId.match(/full-test-(\d+)/);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((value) => Number.isInteger(value))
        .sort((a, b) => a - b)
        .slice(-2);

      const pastTests = sortedCompletedNumbers.map((testNumber) => {
        const testId = `full-test-${testNumber}`;
        const milestone = milestones.find((m) => m.test_id === testId);
        return {
          id: testId,
          label: milestone?.test_assignment || `Full Mock Test #${testNumber}`,
          status: 'completed',
          path: `/exams/gre/full/${testId}`,
          reviewPath: `/exams/gre/full/${testId}?review=true`,
        };
      });

      // Get current test (if not all done)
      const currentTest = !allTestsDone
        ? {
            id: nextTestId,
            label: nextTestLabel,
            status: 'current',
            nextTestDate: nextTestDate,
            targetDate: targetDate,
            isOverdue: isOverdue,
            path: `/exams/gre/full/${nextTestId}`,
          }
        : null;

      return {
        durationWeeks: getEffectiveGrePlanWeeks(plan),
        daily: {
          verbal: [
            {
              id: 'daily-1',
              label: 'Reading',
              description: `${articlesPerDay} articles`,
              path: '/exams/gre',
              state: { activeCategory: 'vocabularymastery' },
            },
            {
              id: 'daily-2',
              label: 'Vocabulary',
              description: `${wordsPerDay} new words`,
              path: '/exams/gre',
              state: { activeCategory: 'vocabularymastery' },
            },
            {
              id: 'daily-3',
              label: 'Verbal Exercises',
              description: 'Text Comp. + Sent. Eq.',
              path: '/exams/gre',
              state: { activeCategory: 'textcompletion' },
            },
          ],
          estimatedTime: '~45 min',
        },
        weekend: {
          saturday: {
            verbal: [
              {
                id: 'weekend-sat-verbal-1',
                label: 'Vocabulary Challenge',
                description: 'Weekly word review',
                path: '/exams/gre/verbal/vocabulary-book?challenge=true',
              },
              {
                id: 'weekend-sat-verbal-2',
                label: 'Flashcard Review',
                description: 'Spaced repetition',
                path: '/exams/gre/verbal/vocabulary/flashcards?review=true',
              },
              {
                id: 'weekend-sat-verbal-3',
                label: 'Article Revision',
                description: 'Every 2 weeks · revisit completed articles',
                path: '/exams/gre/verbal/vocabulary/history',
              },
            ],
            quant: [
              {
                id: 'weekend-sat-quant-1',
                label: `Quant Module ${quantWeekNumber}/${QUANT_WEEKEND_MODULES.length}`,
                description: `${quantModule.label} · ${getQuantTrackLabel(quantTrack)}`,
                path: `/exams/gre/concepts/${quantModule.lessonId}`,
              },
            ],
            estimatedTime: '~2h',
          },
          sunday: {
            verbal: [
              {
                id: 'weekend-sun-verbal-0',
                label: 'Nuance Discovery',
                description: '23 nuanced words',
                path: '/exams/gre/verbal/vocabulary-book?nuance-activity=true',
              },
              {
                id: 'weekend-sun-verbal-1',
                label: 'Reading Comprehension',
                description: 'Full test',
                path: '/exams/gre',
                state: { activeCategory: 'reading' },
              },
            ],
            quant: [
              {
                id: 'weekend-sun-quant-1',
                label: 'Sunday Quant Practice',
                description: quantSundayPractice,
                path: '/exams/gre',
                state: {
                  activeCategory: 'practice',
                  quantPracticeInitialTab: 'custom',
                  quantPracticeTopics: quantModule.topicIds,
                },
              },
            ],
            estimatedTime: '~1h45',
          },
          note: '+ Daily articles, Text Completion & Sentence Equivalence each day · Article Revision every 2 weeks (2nd Saturday)',
        },
        biweekly: {
          nextTestDate: nextTestDate,
          testId: nextTestId,
          label: nextTestLabel,
          description: 'Verbal + Quant + AWA',
          duration: '2h',
          path: `/exams/gre/full/${nextTestId}`,
          frequency: plan?.dailyTasks?.fullTests?.frequency || 'Every 2 weeks',
          completedTests: completedTests,
          totalTests: totalTests,
          allTestsDone: allTestsDone,
          pastTests: pastTests,
          currentTest: currentTest,
        },
        completion: plan?.completion_percentages || {
          verbal_complete: '0%',
          quant_complete: '0%',
          full_tests_complete: '0%',
        },
      };
    } catch (error) {
      console.error('Error building training plan:', error);
      return null;
    }
  }, [
    getArticlesPerDay,
    currentTime,
    getCompletedFullTestsFromLocalStorage,
    getLatestCompletedFullTestNumber,
    user?.email,
  ]);

  // Load real training plan on mount (and when currentTime changes for testing)
  useEffect(() => {
    const planData = buildRealTrainingPlan();
    setRealTrainingPlan(planData);
  }, [buildRealTrainingPlan]);

  // Check mandatory onboarding tasks (GRE Diagnostic + IELTS Assessment)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const cachedMandatory = user?.email
        ? readHomeSnapshot(user.email)?.slices?.mandatory
        : null;
      const cachedIsComplete =
        isHomeSliceFresh(cachedMandatory, HOME_SNAPSHOT_TTLS_MS.mandatory) &&
        Boolean(cachedMandatory.data?.ieltsAssessmentCompleted);
      if (
        isHomeSliceFresh(cachedMandatory, HOME_SNAPSHOT_TTLS_MS.mandatory) &&
        cachedIsComplete
      ) {
        const cached = cachedMandatory.data || {};
        setMandatoryTasks({
          greDiagnostic: {
            completed: ieltsOnlyUser
              ? true
              : Boolean(cached.greDiagnosticCompleted),
            path: '/exams/gre/full/full-test-1',
          },
          ieltsAssessment: {
            completed: greOnlyUser
              ? true
              : Boolean(cached.ieltsAssessmentCompleted),
            path: '/exams/english/speaking',
          },
        });
        return;
      }

      // Check GRE Diagnostic Test (Test 1)
      let greDiagnosticCompleted = false;
      const trainingPlanData = localStorage.getItem('liwaza-training-plan');
      if (trainingPlanData) {
        try {
          const plan = JSON.parse(trainingPlanData);
          const test1 = plan?.milestones?.find(
            (m) => m.test_id === 'full-test-1'
          );
          greDiagnosticCompleted = test1?.milestone_status === 'completed';
        } catch (e) {
          console.error('Error checking GRE diagnostic:', e);
        }
      }

      // Fallback: local GRE test completion signals used elsewhere in the app
      if (!greDiagnosticCompleted) {
        const completedLocalTests = getCompletedFullTestsFromLocalStorage();
        greDiagnosticCompleted = completedLocalTests.some(
          (test) => test.id === 'full-test-1'
        );
      }

      // Fallback: backend GRE completion (cross-device / stale local plan)
      if (!greDiagnosticCompleted && user?.email) {
        try {
          const data = await fetchGreTestResultsRaw('full-test-1');
          const backendTest = data?.success ? data.test : null;
          greDiagnosticCompleted = Boolean(
            backendTest &&
              (backendTest.end_time ||
                backendTest.status === 'completed' ||
                backendTest.detailed_results ||
                backendTest.scaled_score !== null)
          );
        } catch (e) {
          // ignore
        }
      }

      // Check IELTS Assessment:
      // - any IELTS test completed locally
      // - OR user has an enrolled English journey
      // - OR backend has speaking assessment results
      let ieltsAssessmentCompleted = false;

      if (greOnlyUser) {
        ieltsAssessmentCompleted = true;
      }

      // If a language journey exists + enrolled, assessment has effectively been completed
      if (!ieltsAssessmentCompleted && user?.email) {
        const journeyData = localStorage.getItem(
          `languageJourney_${user.email}`
        );
        if (journeyData) {
          try {
            const journey = JSON.parse(journeyData);
            if (journey?.enrolled) ieltsAssessmentCompleted = true;
          } catch (e) {
            // ignore
          }
        }
      }

      if (!ieltsAssessmentCompleted && user?.email) {
        try {
          const profile = await fetchIeltsProfile();
          if (isIeltsProfileAssessed(profile)) {
            ieltsAssessmentCompleted = true;
          }
        } catch (e) {
          // ignore
        }
      }

      // Fallback: check backend speaking assessment (cross-device)
      if (!ieltsAssessmentCompleted && user?.email) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/get-speaking-test-results?testType=speaking`,
            {
              headers: getAuthHeaders(),
            }
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.results && data.results.length > 0) {
              ieltsAssessmentCompleted = true;
            }
          }
        } catch (e) {
          // ignore
        }
      }

      if (cancelled) return;

      if (user?.email) {
        writeHomeSnapshotSlice(user.email, 'mandatory', {
          greDiagnosticCompleted,
          ieltsAssessmentCompleted,
        });
      }

      setMandatoryTasks({
        greDiagnostic: {
          completed: ieltsOnlyUser ? true : greDiagnosticCompleted,
          path: '/exams/gre/full/full-test-1',
        },
        ieltsAssessment: {
          completed: ieltsAssessmentCompleted,
          path: '/exams/english/speaking',
        },
      });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    user?.email,
    greOnlyUser,
    ieltsOnlyUser,
    getCompletedFullTestsFromLocalStorage,
  ]);

  // 7. Fetch verbal progress from localStorage cache or API
  const fetchVerbalProgress = useCallback(async () => {
    if (!user?.email) return null;

    try {
      const snapshotSlice = readHomeSnapshot(user.email)?.slices?.verbalProgress;
      if (
        isHomeSliceFresh(snapshotSlice, HOME_SNAPSHOT_TTLS_MS.verbalProgress)
      ) {
        setVerbalProgress(snapshotSlice.data);
        return snapshotSlice.data;
      }

      const cacheKey = `liwaza_verbal_progress_${user.email}`;
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (
          data &&
          Date.now() - timestamp < HOME_SNAPSHOT_TTLS_MS.verbalProgress
        ) {
          setVerbalProgress(data);
          writeHomeSnapshotSlice(user.email, 'verbalProgress', data, {
            now: timestamp,
          });
          return data;
        }
      }

      const response = await fetch(
        `${API_BASE_URL}/api/gre/verbal/progress?userEmail=${encodeURIComponent(user.email)}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setVerbalProgress(data);
        const savedAt = Date.now();
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ data, timestamp: savedAt, userEmail: user.email })
        );
        writeHomeSnapshotSlice(user.email, 'verbalProgress', data, {
          now: savedAt,
        });
        return data;
      }
    } catch (error) {
      console.error('Error fetching verbal progress:', error);
    }
    return null;
  }, [user?.email]);

  // 8. Build dynamic tasks based on day of week
  const buildDynamicTasks = useCallback(async () => {
    const anchorDate = dateOverride || currentTime;
    const effectiveDate = dateForHomeProgramOffset(
      anchorDate,
      selectedProgramOffset,
      programWindowDays
    );
    let journeyHoldIntervalsPromise = null;
    const journeyHoldIntervals = () => {
      if (!journeyHoldIntervalsPromise) {
        journeyHoldIntervalsPromise = loadJourneyHoldIntervals();
      }
      return journeyHoldIntervalsPromise;
    };
    const dayOfWeek = effectiveDate.getDay(); // 0=Sunday, 6=Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const weekNumber = getWeekNumberInCycle(effectiveDate);
    const isSecondWeekend = isWeekend && weekNumber === 2;
    const articlesPerDay = getArticlesPerDay();
    const quantTrack = getQuantTrack();
    let quantWeekNumber = 1;
    try {
      const trainingPlanData = localStorage.getItem('liwaza-training-plan');
      const plan = trainingPlanData ? JSON.parse(trainingPlanData) : null;
      const planStartDate = plan?.generatedAt
        ? new Date(plan.generatedAt)
        : effectiveDate;
      const daysSincePlanStart = Math.max(
        0,
        Math.floor((effectiveDate - planStartDate) / (1000 * 60 * 60 * 24))
      );
      quantWeekNumber = getEffectiveQuantWeekNumber(
        Math.floor(daysSincePlanStart / 7) + 1,
        quantTrack
      );
    } catch {
      quantWeekNumber = getEffectiveQuantWeekNumber(1, quantTrack);
    }
    const quantModule = getQuantWeekendModule(quantWeekNumber);
    const quantSundayPractice = getSundayPracticeStageLabel(quantWeekNumber);
    const quantDifficultyMix = getSundayDifficultyMix(quantWeekNumber);

    let toeflState = null;
    try {
      toeflState = await fetchToeflBootcamp();
      setToeflBootcamp(toeflState);
    } catch {
      toeflState = null;
      setToeflBootcamp(null);
    }
    const toeflActive = String(toeflState?.status || '').toLowerCase() === 'active';

    const greTasks = [];
    const ieltsTasks = [];
    const priorityTasks = [];
    const weekendTasks = [];
    const mbaAppTasks = [];

    // Use computed mandatory tasks (covers journey + backend speaking results)
    const greDiagnosticDone = mandatoryTasks.greDiagnostic.completed;
    const ieltsAssessmentDone = mandatoryTasks.ieltsAssessment.completed;

    // Add mandatory tasks to priority if not completed
    if (!ieltsOnlyUser && !greDiagnosticDone) {
      priorityTasks.push({
        id: 'mandatory-gre-diagnostic',
        label: '🎯 GRE Diagnostic Test',
        subtext: 'Required · Know your starting level (~2h)',
        path: '/exams/gre/full/full-test-1',
      });
    }

    if (!greOnlyUser && !ieltsAssessmentDone) {
      priorityTasks.push({
        id: 'mandatory-ielts-assessment',
        label: '📝 IELTS Assessment',
        subtext: 'Required · Evaluate your English level',
        path: '/exams/english/speaking',
      });
    }

    // Get verbal progress data
    const vProgress = verbalProgress || (await fetchVerbalProgress());
    const vocabData = vProgress?.vocabulary_mastery;
    const tcData = vProgress?.text_completion;
    const seData = vProgress?.sentence_equivalence;
    const masteredWordsCount =
      vocabData?.total_words_mastered ??
      vocabData?.masteredWords ??
      vocabData?.mastered_words?.length;
    const unlockedGreVerbalExerciseCount =
      getUnlockedGreVerbalExerciseCount(masteredWordsCount);
    const textCompletionTodayTarget = frozenGreExerciseTarget(
      todayProgress,
      'text_completion',
      unlockedGreVerbalExerciseCount
    );
    const sentenceEquivalenceTodayTarget = frozenGreExerciseTarget(
      todayProgress,
      'sentence_equivalence',
      unlockedGreVerbalExerciseCount
    );
    setUnlockedGreCount(unlockedGreVerbalExerciseCount);
    const vocabToday = getDailyVocabTaskProgress(dailyArticles, user?.email);
    const completedArticlesCount =
      vocabData?.completed_articles_count ??
      vocabData?.articles_read ??
      0;
    const hasArticlesToRevise = completedArticlesCount > 0;

    if (!isWeekend) {
      // ==================== WEEKDAY TASKS (Mon-Fri) ====================

      // GRE Verbal: selected-day articles, or the daily quota while they load
      const selectedGreArticles = greVocabHomeTasksFromArticles(dailyArticles);
      if (selectedGreArticles.length > 0) {
        greTasks.push(...selectedGreArticles);
      } else if (selectedProgramOffset !== 0) {
        greTasks.push({
          id: 'gre-vocab-empty',
          label: 'No GRE articles for this day',
          path: '/exams/gre',
          state: { activeCategory: 'vocabularymastery' },
        });
      } else {
        const wordsPerDay = articlesPerDay * 6;
        greTasks.push({
          id: 'gre-vocab',
          label: `${articlesPerDay} Articles + ${wordsPerDay} words`,
          sublabel:
            vocabToday.total > 0
              ? `${vocabToday.completed}/${vocabToday.total} articles done`
              : vocabData
                ? `${vocabData.articles_read || 0} articles read`
                : null,
          path: '/exams/gre',
          state: { activeCategory: 'vocabularymastery' },
        });
      }

      // GRE Verbal: Text Completion
      greTasks.push({
        id: 'gre-tc',
        label: formatGreExerciseTaskLabel(
          'Text Completion',
          textCompletionTodayTarget
        ),
        sublabel:
          typeof textCompletionTodayTarget === 'number'
            ? `${todayProgress?.text_completion_attempted_today || 0}/${textCompletionTodayTarget} today`
            : tcData
              ? `Accuracy: ${Math.round(tcData.overall_accuracy || 0)}%`
              : null,
        path: '/exams/gre',
        state: { activeCategory: 'textcompletion' },
      });

      // GRE Verbal: Sentence Equivalence
      greTasks.push({
        id: 'gre-se',
        label: formatGreExerciseTaskLabel(
          'Sentence Equivalence',
          sentenceEquivalenceTodayTarget
        ),
        sublabel:
          typeof sentenceEquivalenceTodayTarget === 'number'
            ? `${todayProgress?.sentence_equivalence_attempted_today || 0}/${sentenceEquivalenceTodayTarget} today`
            : seData
              ? `Accuracy: ${Math.round(seData.overall_accuracy || 0)}%`
              : null,
        path: '/exams/gre',
        state: { activeCategory: 'sentence' },
      });

      // IELTS: Get from language journey (hidden on Today while TOEFL bootcamp is active)
      const journeyData = localStorage.getItem(
        `languageJourney_${user?.email}`
      );
      if (!greOnlyUser && !toeflActive && journeyData) {
        try {
          const journey = JSON.parse(journeyData);
          // Note: journey data uses 'journey' key, not 'plan'
          const journeyPlan = journey?.journey || journey?.plan;
          if (journey?.enrolled && journeyPlan) {
            // Calculate current day dynamically based on start date (same as LanguageJourney.js)
            // Pass user email to check for admin day override
            const holdIntervals = await journeyHoldIntervals();
            const currentDay = journey.startDate
              ? getCurrentJourneyDay(
                  journey.startDate,
                  user?.email,
                  journeyPlan.length || 60,
                  holdIntervals
                )
              : journey.currentDay || 1;
            const selectedDay = resolveSelectedJourneyDay(
              currentDay,
              selectedProgramOffset,
              journeyPlan.length
            );
            const todayPlan = selectedDay
              ? journeyPlan.find((d) => d.day === selectedDay)
              : null;

            if (todayPlan) {
              // Speaking practice - show even if completed (will be marked as done in UI)
              const speakingActivity = todayPlan.activities?.find(
                (a) => a.type === 'speaking'
              );
              if (speakingActivity) {
                // Truncate topic if too long for display
                const shortTopic =
                  speakingActivity.topic && speakingActivity.topic.length > 35
                    ? speakingActivity.topic.substring(0, 35) + '...'
                    : speakingActivity.topic;

                ieltsTasks.push({
                  id: 'ielts-speaking',
                  label: `Speaking: ${speakingActivity.category || 'Daily Practice'}`,
                  sublabel: shortTopic
                    ? `${activityDurationMinutes(speakingActivity)} min · Speaking practice · ${shortTopic}`
                    : formatIeltsActivitySublabel(speakingActivity),
                  path: speakingActivity.path || '/exams/english/speaking',
                  state: {
                    fromJourney: true,
                    topic: speakingActivity.topic,
                    topicCategory: speakingActivity.category,
                    journeyActivityId: speakingActivity.id,
                    topicId: speakingActivity.topicId,
                  },
                  // Track if already completed in journey data
                  preCompleted: speakingActivity.completed || false,
                });
              }

              // Course of the day - show even if completed (will be marked as done in UI)
              const courseActivity = todayPlan.activities?.find(
                (a) => a.type !== 'speaking' && !a.isTest
              );
              if (courseActivity) {
                ieltsTasks.push({
                  id: 'ielts-course',
                  label: formatIeltsActivityTitle(courseActivity),
                  sublabel: formatIeltsActivitySublabel(courseActivity),
                  path: courseActivity.path || '/exams/english',
                  // Track if already completed in journey data
                  preCompleted: courseActivity.completed || false,
                });
              }
            }
          }
        } catch (error) {
          console.error('Error parsing journey data:', error);
        }
      }

      // Weekend tasks go to "More" section on weekdays
      weekendTasks.push(
        {
          id: 'weekend-vocab',
          label: 'Vocabulary Challenge',
          path: '/exams/gre/verbal/vocabulary-book?challenge=true',
        },
        {
          id: 'weekend-nuance',
          label: 'Nuance Discovery',
          path: '/exams/gre/verbal/vocabulary-book?nuance-activity=true',
        },
        {
          id: 'weekend-flashcard',
          label: 'Flashcard Review',
          path: '/exams/gre/verbal/vocabulary/flashcards?review=true',
        },
        ...(hasArticlesToRevise && weekNumber === 2
          ? [
              {
                id: 'weekend-article-revision',
                label: 'Article Revision (bi-weekly)',
                path: '/exams/gre/verbal/vocabulary/history',
              },
            ]
          : []),
        {
          id: 'weekend-reading',
          label: 'Reading Comprehension Test',
          path: '/exams/gre',
          state: { activeCategory: 'reading' },
        },
        {
          id: 'weekend-quant',
          label: `Quant Module ${quantWeekNumber}/${QUANT_WEEKEND_MODULES.length}`,
          sublabel: `${quantModule.label} · ${getQuantTrackLabel(quantTrack)}`,
          path: `/exams/gre/concepts/${quantModule.lessonId}`,
        }
      );
    } else {
      // ==================== WEEKEND TASKS (Sat-Sun) ====================
      // Daily tasks (articles + TC + SE) are required EVERY day, including weekends
      const isSaturday = dayOfWeek === 6;

      // Daily verbal tasks - selected-day articles, including weekends
      const selectedGreArticles = greVocabHomeTasksFromArticles(dailyArticles);
      if (selectedGreArticles.length > 0) {
        greTasks.push(...selectedGreArticles);
      } else if (selectedProgramOffset !== 0) {
        greTasks.push({
          id: 'gre-vocab-empty',
          label: 'No GRE articles for this day',
          path: '/exams/gre',
          state: { activeCategory: 'vocabularymastery' },
        });
      } else {
        const wordsPerDay = articlesPerDay * 6;
        greTasks.push({
          id: 'gre-vocab',
          label: `${articlesPerDay} Articles + ${wordsPerDay} words`,
          sublabel:
            vocabToday.total > 0
              ? `${vocabToday.completed}/${vocabToday.total} articles done`
              : vocabData
                ? `${vocabData.articles_read || 0} articles read`
                : null,
          path: '/exams/gre',
          state: { activeCategory: 'vocabularymastery' },
        });
      }

      greTasks.push({
        id: 'gre-tc',
        label: formatGreExerciseTaskLabel(
          'Text Completion',
          textCompletionTodayTarget
        ),
        sublabel:
          typeof textCompletionTodayTarget === 'number'
            ? `${todayProgress?.text_completion_attempted_today || 0}/${textCompletionTodayTarget} today`
            : tcData
              ? `Accuracy: ${Math.round(tcData.overall_accuracy || 0)}%`
              : null,
        path: '/exams/gre',
        state: { activeCategory: 'textcompletion' },
      });

      greTasks.push({
        id: 'gre-se',
        label: formatGreExerciseTaskLabel(
          'Sentence Equivalence',
          sentenceEquivalenceTodayTarget
        ),
        sublabel:
          typeof sentenceEquivalenceTodayTarget === 'number'
            ? `${todayProgress?.sentence_equivalence_attempted_today || 0}/${sentenceEquivalenceTodayTarget} today`
            : seData
              ? `Accuracy: ${Math.round(seData.overall_accuracy || 0)}%`
              : null,
        path: '/exams/gre',
        state: { activeCategory: 'sentence' },
      });

      // Weekend-specific tasks split between Saturday and Sunday
      if (isSaturday) {
        // === SATURDAY: Vocabulary review + Quant lesson ===
        greTasks.push({
          id: 'gre-vocab-challenge',
          label: 'Vocabulary Challenge',
          sublabel: 'Review words from the week',
          path: '/exams/gre/verbal/vocabulary-book?challenge=true',
        });

        greTasks.push({
          id: 'gre-flashcard-review',
          label: 'Flashcard Review',
          sublabel: 'Spaced repetition practice',
          path: '/exams/gre/verbal/vocabulary/flashcards?review=true',
        });

        // Article revision only on the 2nd Saturday of the 2-week cycle
        if (weekNumber === 2 && hasArticlesToRevise) {
          greTasks.push({
            id: 'gre-article-revision',
            label: 'Article Revision',
            sublabel: `Every 2 weeks · ${completedArticlesCount} past articles available`,
            path: '/exams/gre/verbal/vocabulary/history',
          });
        }

        greTasks.push({
          id: 'gre-quant-course',
          label: `Quant Module ${quantWeekNumber}/${QUANT_WEEKEND_MODULES.length}`,
          sublabel: `${quantModule.label} · ${getQuantTrackLabel(quantTrack)}`,
          path: `/exams/gre/concepts/${quantModule.lessonId}`,
        });
      } else {
        // === SUNDAY: Reading comprehension + Quant exercises + Nuance Discovery ===
        greTasks.push({
          id: 'gre-nuance-review',
          label: 'Nuance Discovery',
          sublabel: '23 nuanced words to master',
          path: '/exams/gre/verbal/vocabulary-book?nuance-activity=true',
        });

        greTasks.push({
          id: 'gre-reading',
          label: 'Reading Comprehension Test',
          sublabel: 'Full passage practice',
          path: '/exams/gre',
          state: { activeCategory: 'reading' },
        });

        greTasks.push({
          id: 'gre-quant-exercises',
          label: 'Sunday Quant Practice',
          sublabel: quantSundayPractice,
          path: '/exams/gre',
          state: {
            activeCategory: 'practice',
            quantPracticeInitialTab: 'custom',
            quantPracticeTopics: quantModule.topicIds,
          },
        });
      }

      // GRE Mock Test (only on 2nd weekend Saturday)
      if (isSecondWeekend && isSaturday) {
        const trainingPlanData = localStorage.getItem('liwaza-training-plan');
        let totalTests = 10;
        if (trainingPlanData) {
          try {
            const plan = JSON.parse(trainingPlanData);
            totalTests = plan?.milestones?.length || 10;
          } catch (e) {
            console.error('Error parsing training plan:', e);
          }
        }

        const completedLocalTests = getCompletedFullTestsFromLocalStorage();
        const completedLocalIds = completedLocalTests.map((test) => test.id);
        const latestCompletedNumber =
          getLatestCompletedFullTestNumber(completedLocalIds);
        const nextTestNumber = Math.min(totalTests, latestCompletedNumber + 1);
        const nextTestId = `full-test-${nextTestNumber}`;

        // Add to GRE tasks
        greTasks.push({
          id: 'gre-mock-test',
          label: `🎯 Full GRE Mock Test #${nextTestNumber}`,
          sublabel: 'Bi-weekly full test · ~2h',
          path: `/exams/gre/full/${nextTestId}`,
        });

        // Add to priority tasks
        priorityTasks.push({
          id: 'gre-mock-test-priority',
          label: `🎯 GRE Mock Test #${nextTestNumber}`,
          subtext: 'Bi-weekly full test · ~2h',
          path: `/exams/gre/full/${nextTestId}`,
        });
      }

      // IELTS: Weekend activities from journey
      const journeyDataWeekend = localStorage.getItem(
        `languageJourney_${user?.email}`
      );
      if (!greOnlyUser && journeyDataWeekend) {
        try {
          const journey = JSON.parse(journeyDataWeekend);
          // Note: journey data uses 'journey' key, not 'plan'
          const journeyPlan = journey?.journey || journey?.plan;
          if (journey?.enrolled && journeyPlan) {
            // Calculate current day dynamically based on start date (same as LanguageJourney.js)
            // Pass user email to check for admin day override
            const holdIntervals = await journeyHoldIntervals();
            const currentDay = journey.startDate
              ? getCurrentJourneyDay(
                  journey.startDate,
                  user?.email,
                  journeyPlan.length || 60,
                  holdIntervals
                )
              : journey.currentDay || 1;
            const selectedDay = resolveSelectedJourneyDay(
              currentDay,
              selectedProgramOffset,
              journeyPlan.length
            );
            const todayPlan = selectedDay
              ? journeyPlan.find((d) => d.day === selectedDay)
              : null;

            if (todayPlan) {
              todayPlan.activities?.forEach((activity) => {
                const homeTask = formatIeltsHomeTask(activity);
                ieltsTasks.push({
                  id: `ielts-${activity.id}`,
                  label: homeTask.label,
                  sublabel: homeTask.sublabel,
                  path: activity.path || '/exams/english',
                  // Track if already completed in journey data
                  preCompleted: activity.completed || false,
                });
              });
            }
          }
        } catch (error) {
          console.error('Error parsing journey data:', error);
        }
      }
    }

    const primarySchool = admissionsHome?.primary?.school_name;

    if (toeflActive) {
      try {
        const daily = await fetchToeflDailyContent();
        const next = daily?.next;
        if (next) {
          ieltsTasks.push({
            id: `toefl-today-${next.lesson_id || next.item_id || next.kind}`,
            label: next.title || (next.kind === 'strategy_lesson' ? 'TOEFL how-to lesson' : 'TOEFL practice'),
            sublabel: next.skill
              ? `${next.skill}${next.task_type ? ` · ${String(next.task_type).replace(/_/g, ' ')}` : ''}`
              : 'TOEFL iBT 2026',
            path: next.route || '/exams/toefl',
          });
        } else {
          ieltsTasks.push({
            id: 'toefl-hub',
            label: 'TOEFL bootcamp',
            sublabel: 'Open today’s practice',
            path: '/exams/toefl/hub',
          });
        }
      } catch {
        ieltsTasks.push({
          id: 'toefl-hub',
          label: 'TOEFL bootcamp',
          sublabel: 'Open today’s practice',
          path: '/exams/toefl/hub',
        });
      }
    }
    mbaAppTasks.push(
      ...buildApplicationHomeTasks({
        targetingComplete: Boolean(admissionsHome?.complete),
        primarySchool,
        completion: applicationWorkspace?.completion || {},
      })
    );

    // Check for deadline-based priority tasks
    if (deadlines.length > 0 && deadlines[0].daysLeft < 45) {
      priorityTasks.push({
        id: 'priority-deadline',
        label: `Finalize Application (${deadlines[0].school})`,
        subtext: `Deadline in ${deadlines[0].daysLeft} days`,
        path: '/applications',
      });
    }

    const greProgramUnlocked = ieltsOnlyUser || greDiagnosticDone;
    const visiblePriorityTasks = greProgramUnlocked
      ? priorityTasks
      : priorityTasks.filter((task) => task.id !== 'gre-mock-test-priority');
    const visibleGreTasks = greProgramUnlocked
      ? greTasks
      : greTasks.filter(
          (task) =>
            String(task.id).startsWith('gre-article-') ||
            task.id === 'gre-vocab' ||
            task.id === 'gre-vocab-empty'
        );

    setTodayTasksData({
      gre: visibleGreTasks,
      ielts: ieltsTasks,
      priority: visiblePriorityTasks,
    });
    setMoreTasksData({
      mbaApp: mbaAppTasks,
      weekend: greProgramUnlocked ? weekendTasks : [],
    });
  }, [
    user?.email,
    verbalProgress,
    deadlines,
    getWeekNumberInCycle,
    getArticlesPerDay,
    fetchVerbalProgress,
    currentTime,
    getCompletedFullTestsFromLocalStorage,
    getLatestCompletedFullTestNumber,
    dateOverride, // Add dateOverride as dependency
    mandatoryTasks,
    greOnlyUser,
    ieltsOnlyUser,
    dailyArticles,
    selectedProgramOffset,
    programWindowDays,
    todayProgress,
    admissionsHome,
    applicationWorkspace,
  ]);

  const applyHomeSnapshot = useCallback((email, dateKey) => {
    const snapshot = readHomeSnapshot(email);
    if (!snapshot?.slices) return false;
    const { admissions, verbalProgress: verbal, dailyArticles: articles, todayProgress: today, community } =
      snapshot.slices;
    let applied = false;

    if (admissions?.data) {
      if (admissions.data.home) setAdmissionsHome(admissions.data.home);
      if (admissions.data.workspace) {
        setApplicationWorkspace(admissions.data.workspace);
      }
      if (Array.isArray(admissions.data.deadlines)) {
        setDeadlines(admissions.data.deadlines);
      }
      applied = true;
    }
    if (verbal?.data) {
      setVerbalProgress(verbal.data);
      applied = true;
    }
    if (articles?.dateKey === dateKey && Array.isArray(articles.data)) {
      setDailyArticles(articles.data);
      applied = true;
    }
    if (today?.dateKey === dateKey && today.data) {
      setTodayProgress(today.data);
      applied = true;
    }
    if (community?.data && typeof community.data.newQuestions === 'number') {
      setCommunityData((prev) => ({
        ...(prev || {
          newQuestions: 0,
          newReplies: 0,
          upcomingEvent: null,
          upcomingWebinar: null,
        }),
        newQuestions: community.data.newQuestions,
      }));
      applied = true;
    }
    return applied;
  }, []);

  // Load all data on mount. A saved snapshot paints immediately; network
  // runs only for slices whose TTL, or calendar day, has expired.
  useEffect(() => {
    let cancelled = false;
    const dateKey = getLocalDateKey(dateOverride || new Date());
    const snapshot = user?.email ? readHomeSnapshot(user.email) : null;
    const hadCache = user?.email ? applyHomeSnapshot(user.email, dateKey) : false;
    if (hadCache) setDataLoading(false);
    else setDataLoading(true);

    const loadAllData = async () => {
      fetchGreData();
      fetchIeltsData();
      readHomeSummaryFromLocalStorage();

      const slices = snapshot?.slices || {};
      if (
        isHomeSliceFresh(slices.community, HOME_SNAPSHOT_TTLS_MS.community) &&
        typeof slices.community.data?.newQuestions === 'number'
      ) {
        setCommunityData((prev) => ({
          ...(prev || {
            newQuestions: 0,
            newReplies: 0,
            upcomingEvent: null,
            upcomingWebinar: null,
          }),
          newQuestions: slices.community.data.newQuestions,
        }));
      }
      const jobs = [];
      if (!isHomeSliceFresh(slices.community, HOME_SNAPSHOT_TTLS_MS.community)) {
        jobs.push(fetchCommunityQuestionStats());
      }
      if (!isHomeSliceFresh(slices.admissions, HOME_SNAPSHOT_TTLS_MS.admissions)) {
        jobs.push(fetchDeadlines());
      }
      if (
        !isHomeSliceFresh(slices.verbalProgress, HOME_SNAPSHOT_TTLS_MS.verbalProgress)
      ) {
        jobs.push(fetchVerbalProgress());
      }
      jobs.push(
          loadHomeProgramWindows()
            .then(({ greDays, journeyDays, greFailed }) => {
              if (cancelled) return;
              setJourneyWindowDays(journeyDays);
              if (greFailed) {
                setGreWindowDays([]);
                return fetchDailyArticlesForHome()
                  .then((articles) => {
                    if (cancelled) return;
                    const list = Array.isArray(articles) ? articles : [];
                    setDailyArticles(list);
                  })
                  .catch(() => {});
              }
              setGreWindowDays(greDays);
              const list = articlesFromProgramWindow(
                { days: greDays },
                selectedProgramOffset
              );
              setDailyArticles(list);
              if (user?.email) {
                writeHomeSnapshotSlice(user.email, 'dailyArticles', list, {
                  dateKey,
                });
              }
            })
        );
      if (
        !isHomeSliceFresh(slices.todayProgress, HOME_SNAPSHOT_TTLS_MS.todayProgress, {
          dateKey,
        })
      ) {
        jobs.push(
          fetchHomeTodayProgress(dateKey)
            .then((progress) => {
              if (cancelled) return;
              setTodayProgress(progress);
              if (user?.email) {
                writeHomeSnapshotSlice(user.email, 'todayProgress', progress, {
                  dateKey,
                });
              }
            })
            .catch(() => {})
        );
      }

      await Promise.all(jobs);
      if (!cancelled) setDataLoading(false);
    };

    loadAllData();
    return () => {
      cancelled = true;
    };
  }, [
    user?.email,
    dateOverride,
    applyHomeSnapshot,
    fetchGreData,
    fetchIeltsData,
    readHomeSummaryFromLocalStorage,
    fetchCommunityQuestionStats,
    fetchDeadlines,
    fetchVerbalProgress,
  ]);

  // Build dynamic tasks when data is loaded
  useEffect(() => {
    if (!dataLoading) {
      buildDynamicTasks();
    }
  }, [dataLoading, buildDynamicTasks]);

  // ========== COMPUTED DATA ==========

  // Fallback data structure (used when real data is loading or unavailable)
  const defaultData = {
    gre: greData || {
      currentScore: 285,
      maxScore: 340,
      verbal: 130,
      quant: 130,
      awa: 3.0,
      targetScore: 320,
      targetVerbal: 160,
      targetQuant: 162,
      targetAwa: 4.0,
      planWeeks: 12,
    },
    ielts: ieltsData || {
      currentLevel: 'B1',
      targetLevel: 'B2',
      progress: 60,
      speaking: 'B1',
      listening: 'B1+',
      reading: 'B1',
      writing: 'A2',
    },
    deadlines: deadlines.length > 0 ? deadlines : [],
    coaching: coachingData,
    community: communityData || {
      newQuestions: 0,
      newReplies: 0,
      upcomingEvent: null,
      upcomingWebinar: null,
    },
    // Dynamic task data based on day of week
    todayTasks: {
      gre: todayTasksData.gre,
      ielts: todayTasksData.ielts.length > 0 ? todayTasksData.ielts : [],
      priority: todayTasksData.priority,
    },
    moreTasks: {
      mbaApp:
        moreTasksData.mbaApp.length > 0
          ? moreTasksData.mbaApp
          : buildApplicationHomeTasks({
              targetingComplete: Boolean(admissionsHome?.complete),
              primarySchool: admissionsHome?.primary?.school_name,
              completion: applicationWorkspace?.completion || {},
            }),
      weekend: moreTasksData.weekend,
    },
    // Training Plan Data - Use real data if available, fallback to defaults
    trainingPlan: realTrainingPlan || {
      daily: {
        verbal: [
          {
            id: 'daily-1',
            label: 'Reading',
            description: '2 articles',
            path: '/exams/gre',
            state: { activeCategory: 'vocabularymastery' },
          },
          {
            id: 'daily-2',
            label: 'Vocabulary',
            description: '12 new words', // 2 articles × 6 = 12
            path: '/exams/gre',
            state: { activeCategory: 'vocabularymastery' },
          },
          {
            id: 'daily-3',
            label: 'Verbal Exercises',
            description: 'Text Comp. + Sent. Eq.',
            path: '/exams/gre',
            state: { activeCategory: 'textcompletion' },
          },
        ],
        estimatedTime: '~45 min',
      },
      weekend: {
        saturday: {
          verbal: [
            {
              id: 'weekend-sat-verbal-1',
              label: 'Vocabulary Challenge',
              description: 'Weekly word review',
              path: '/exams/gre/verbal/vocabulary-book?challenge=true',
            },
            {
              id: 'weekend-sat-verbal-2',
              label: 'Flashcard Review',
              description: 'Spaced repetition',
              path: '/exams/gre/verbal/vocabulary/flashcards?review=true',
            },
            {
              id: 'weekend-sat-verbal-3',
              label: 'Article Revision',
              description: 'Every 2 weeks · revisit completed articles',
              path: '/exams/gre/verbal/vocabulary/history',
            },
          ],
          quant: [
            {
              id: 'weekend-sat-quant-1',
              label: 'Weekly Course',
              description: 'New lesson',
              path: '/exams/gre',
              state: { activeCategory: 'concepts' },
            },
          ],
          estimatedTime: '~2h',
        },
        sunday: {
          verbal: [
            {
              id: 'weekend-sun-verbal-0',
              label: 'Nuance Discovery',
              description: '23 nuanced words',
              path: '/exams/gre/verbal/vocabulary-book?nuance-activity=true',
            },
            {
              id: 'weekend-sun-verbal-1',
              label: 'Reading Comprehension',
              description: 'Full test',
              path: '/exams/gre',
              state: { activeCategory: 'reading' },
            },
          ],
          quant: [
            {
              id: 'weekend-sun-quant-1',
              label: 'Quant Exercises',
              description: 'Deep practice',
              path: '/exams/gre',
              state: { activeCategory: 'practice' },
            },
          ],
          estimatedTime: '~1h45',
        },
        note: '+ Daily articles, Text Completion & Sentence Equivalence each day · Article Revision every 2 weeks (2nd Saturday)',
      },
      biweekly: {
        nextTestDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        testId: 'full-test-1',
        label: 'Full GRE Mock Test #1',
        description: 'Verbal + Quant + AWA',
        duration: '~2h',
        path: '/exams/gre/full/full-test-1',
        frequency: 'Every 2 weeks',
        completedTests: 0,
        totalTests: 10,
        allTestsDone: false,
        pastTests: [],
        currentTest: {
          id: 'full-test-1',
          label: 'Full Mock Test #1',
          status: 'current',
          nextTestDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
          path: '/exams/gre/full/full-test-1',
        },
      },
    },
  };

  // Use defaultData as mockData replacement
  const mockData = defaultData;
  const hasDuplicateCommunityHighlight =
    normalizeCommunityHighlightTitle(mockData.community.upcomingEvent?.title) &&
    normalizeCommunityHighlightTitle(mockData.community.upcomingEvent?.title) ===
      normalizeCommunityHighlightTitle(mockData.community.upcomingWebinar?.title);
  const displayedUpcomingEvent = hasDuplicateCommunityHighlight
    ? null
    : mockData.community.upcomingEvent;
  const displayedUpcomingWebinar = mockData.community.upcomingWebinar;

  const handleAddHighlightToCalendar = useCallback((item, type) => {
    if (!item?.date) return;

    downloadCalendarInvite({
      title: item.title || (type === 'webinar' ? 'Liwaza Webinar' : 'Liwaza Event'),
      startDate: item.date,
      durationMinutes: item.duration,
      description:
        type === 'webinar'
          ? 'Upcoming Liwaza webinar'
          : 'Upcoming Liwaza community event',
      path:
        type === 'webinar'
          ? item.slug
            ? `/webinars/join/${item.slug}`
            : '/webinars'
          : '/community',
    });
  }, []);

  // Format date
  const formatDate = (date) => {
    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Calculate days until a date (uses currentTime for testing)
  const getDaysUntil = (targetDate) => {
    const diff = targetDate - currentTime;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Format date for display (e.g., "Saturday, January 25, 2026")
  const formatFullDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate progress percentage for biweekly test
  const getBiweeklyProgress = () => {
    const testDate =
      mockData.trainingPlan.biweekly.currentTest?.nextTestDate ||
      mockData.trainingPlan.biweekly.nextTestDate;
    const daysUntil = getDaysUntil(testDate);
    const totalDays = 14; // 2 weeks
    // If daysUntil is 0 or less (today or past), progress is 100%
    if (daysUntil <= 0) return 100;
    const daysPassed = totalDays - daysUntil;
    return Math.min(100, Math.max(0, (daysPassed / totalDays) * 100));
  };

  const handleTaskClick = useCallback(
    (task) => {
      const effectiveDate = dateOverride || currentTime;
      markHomeTaskEvent(user?.email, task.id, 'started', effectiveDate);

      if (String(task.path || '').includes('#targeting')) {
        const targetingEl = document.getElementById('targeting');
        if (targetingEl) {
          targetingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (window.location.hash !== '#targeting') {
            window.history.replaceState(null, '', '#targeting');
          }
          return;
        }
      }

      if (task.state) {
        navigate(task.path, { state: task.state });
      } else {
        navigate(task.path);
      }
    },
    [currentTime, dateOverride, user?.email, navigate]
  );

  const refreshTaskStatuses = useCallback(() => {
    const effectiveDate = dateOverride || currentTime;
    const allTasks = [
      ...todayTasksData.gre,
      ...todayTasksData.ielts,
      ...todayTasksData.priority,
      ...moreTasksData.mbaApp,
      ...moreTasksData.weekend,
    ];
    setTaskStatuses((prev) => {
      const next = {};
      allTasks.forEach((task) => {
        const status = resolveHomeTaskStatus({
          task,
          email: user?.email,
          date: effectiveDate,
          dailyArticles,
          todayProgress,
          unlockedCount: unlockedGreCount,
        });
        next[task.id] = status == null ? prev[task.id] : status;
      });
      return next;
    });
  }, [
    currentTime,
    dailyArticles,
    dateOverride,
    moreTasksData.mbaApp,
    moreTasksData.weekend,
    todayProgress,
    todayTasksData.gre,
    todayTasksData.ielts,
    todayTasksData.priority,
    unlockedGreCount,
    user?.email,
  ]);

  useEffect(() => {
    refreshTaskStatuses();
  }, [refreshTaskStatuses]);

  useEffect(() => {
    const onFocus = () => {
      const dateKey = getLocalDateKey(dateOverride || new Date());
      const slices = user?.email ? readHomeSnapshot(user.email)?.slices : null;
      if (
        !isHomeSliceFresh(slices?.dailyArticles, HOME_SNAPSHOT_TTLS_MS.dailyArticles, {
          dateKey,
        })
      ) {
        loadHomeProgramWindows().then(({ greDays, journeyDays, greFailed }) => {
          setJourneyWindowDays(journeyDays);
          if (greFailed) {
            setGreWindowDays([]);
            fetchDailyArticlesForHome()
              .then((articles) => {
                const list = Array.isArray(articles) ? articles : [];
                setDailyArticles(list);
              })
              .catch(() => {});
            return;
          }
          setGreWindowDays(greDays);
          const list = articlesFromProgramWindow(
            { days: greDays },
            selectedProgramOffset
          );
          setDailyArticles(list);
          if (user?.email) {
            writeHomeSnapshotSlice(user.email, 'dailyArticles', list, {
              dateKey,
            });
          }
        });
      }
      if (
        !isHomeSliceFresh(slices?.todayProgress, HOME_SNAPSHOT_TTLS_MS.todayProgress, {
          dateKey,
        })
      ) {
        fetchHomeTodayProgress(dateKey)
          .then((progress) => {
            setTodayProgress(progress);
            if (user?.email) {
              writeHomeSnapshotSlice(user.email, 'todayProgress', progress, {
                dateKey,
              });
            }
          })
          .catch(() => {});
        return;
      }
      refreshTaskStatuses();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dateOverride, refreshTaskStatuses, user?.email]);

  // Count more tasks
  const moreTasksCount =
    mockData.moreTasks.mbaApp.length + mockData.moreTasks.weekend.length;

  // Navigation items
  const baseNavItems = [
    { label: 'Home', path: '/home', icon: FiHome },
    { label: 'GRE', path: '/exams/gre', icon: FiBook },
    { label: 'English', path: '/exams/english/', icon: FiMic },
    { label: 'Applications', path: '/applications', icon: FiFileText },
    { label: 'Coaching', path: '/coaching', icon: FiUsers },
    { label: 'Community', path: '/community', icon: FiMessageCircle },
  ];
  const navItems = ieltsOnlyUser
    ? baseNavItems.filter((item) => item.label === 'English')
    : greOnlyUser
      ? baseNavItems.filter((item) => item.label !== 'English')
      : baseNavItems;

  // Check if it's a 2nd weekend Saturday for GRE Mock Test notification
  const effectiveDateForNotifs = dateOverride || currentTime;
  const dayOfWeekForNotifs = effectiveDateForNotifs.getDay();
  const isSaturdayForNotifs = dayOfWeekForNotifs === 6;
  const weekNumberForNotifs = getWeekNumberInCycle();
  const isSecondWeekendSatForNotifs =
    isSaturdayForNotifs && weekNumberForNotifs === 2;

  // Get next test number for notification
  const getNextTestInfo = () => {
    const trainingPlanData = localStorage.getItem('liwaza-training-plan');
    if (!trainingPlanData) return { number: 1, id: 'full-test-1' };
    try {
      const plan = JSON.parse(trainingPlanData);
      const completedLocalTests = getCompletedFullTestsFromLocalStorage();
      const completedLocalIds = completedLocalTests.map((test) => test.id);
      const completedMilestoneIds =
        plan?.milestones
          ?.filter((m) => m.milestone_status === 'completed')
          .map((m) => m.test_id)
          .filter(Boolean) || [];
      const completedTestIds = Array.from(
        new Set([...completedMilestoneIds, ...completedLocalIds])
      );
      const latestCompletedNumber =
        getLatestCompletedFullTestNumber(completedTestIds);
      const totalTests = plan?.milestones?.length || 10;
      const nextTestNumber = Math.min(totalTests, latestCompletedNumber + 1);
      return {
        number: nextTestNumber,
        id: `full-test-${nextTestNumber}`,
      };
    } catch {
      return { number: 1, id: 'full-test-1' };
    }
  };
  const nextTestInfo = getNextTestInfo();

  const notifications = [
    // GRE Mock Test notification (only on 2nd weekend - HIGH PRIORITY, and only if diagnostic is done)
    ...(isSecondWeekendSatForNotifs && mandatoryTasks.greDiagnostic.completed
      ? [
          {
            id: 'gre-mock-test-notif',
            kind: 'gre-test',
            priority: 'high',
            title: `🎯 GRE Mock Test #${nextTestInfo.number}`,
            meta: 'Today · Full test recommended (~2h)',
            onClick: () => navigate(`/exams/gre/full/${nextTestInfo.id}`),
          },
        ]
      : []),
    ...(mockData.deadlines || []).map((d) => ({
      id: `deadline-${d.school}-${d.round}`,
      kind: 'deadline',
      priority: d.priority,
      title: `${d.school} ${d.round}${d.cohort ? ` · ${d.cohort}` : ''}`,
      meta: `Deadline to apply in ${d.daysLeft} day${d.daysLeft !== 1 ? 's' : ''}`,
      onClick: () => navigate('/applications'),
    })),
    ...(mockData.coaching?.nextSession
      ? [
          {
            id: 'coaching-next',
            kind: 'coaching',
            title: 'Coaching',
            meta: `${mockData.coaching.nextSession.date}, ${mockData.coaching.nextSession.time} · @${mockData.coaching.nextSession.coach}`,
            onClick: () => navigate('/coaching'),
          },
        ]
      : []),
  ];
  const notifPreviewLimit = 2;
  const hasMoreNotifs = notifications.length > notifPreviewLimit;
  const visibleNotifs = showAllNotifs
    ? notifications
    : notifications.slice(0, notifPreviewLimit);

  return (
    <div className="cc-command-center">
      {/* Sidebar - visible on mobile/tablet as bottom nav */}
      <Sidebar />

      {/* Top Navigation Bar - hidden on mobile/tablet */}
      <header className="cc-top-nav">
        <div className="cc-top-nav__inner">
          <div className="cc-top-nav__left">
            <Link
              to={user?.is_premium ? getPremiumLandingPath(user) : '/home'}
              className="cc-top-nav__logo"
            >
              <img src="/Liwaza-workspace.svg" alt="Liwaza" />
            </Link>
          </div>

          <nav className="cc-top-nav__center">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                className={`cc-top-nav__link ${window.location.pathname === item.path ? 'cc-active' : ''}`}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="cc-top-nav__right">
            <Link to="/settings" className="cc-top-nav__profile-btn">
              <ProfilePicture
                user={user}
                style={{ width: 32, height: 32, borderRadius: '50%' }}
              />
              <span className="cc-top-nav__profile-label">Profile</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="cc-command-center-content">
        <div className="cc-command-center-grid">
          {/* Left Column */}
          <div className="cc-left-column">
            {/* Date & Time Section */}
            <div className="cc-datetime-section">
              <div className="cc-datetime-header">
                <div className="cc-datetime-display">
                  <FiCalendar size={20} />
                  <div className="cc-datetime-text">
                    <span className="cc-date">{formatDate(currentTime)}</span>
                    <span className="cc-time">{formatTime(currentTime)}</span>
                  </div>
                </div>
                <img
                  src="/Liwaza-workspace.svg"
                  alt="Liwaza Workspace"
                  className="cc-mobile-logo"
                />
              </div>
              <h1 className="cc-greeting">
                Hello {user?.first_name || 'Patrick'}! 👋
              </h1>
              <p className="cc-greeting-subtitle">
                Here's your dashboard for today.
              </p>
              <ProgressHoldBanner />

              {/* Notifications (deadlines + coaching) */}
              <div className="cc-notifications">
                {visibleNotifs.map((n) => (
                  <div
                    key={n.id}
                    className={`cc-notif cc-notif--${n.kind} ${
                      n.priority ? `cc-is-${n.priority}` : ''
                    }`}
                    onClick={n.onClick}
                  >
                    {n.kind === 'deadline' ? (
                      <span className="cc-notif__dot" />
                    ) : n.kind === 'gre-test' ? (
                      <span className="cc-notif__icon">
                        <FiTarget size={14} />
                      </span>
                    ) : n.kind === 'ielts' ? (
                      <span className="cc-notif__icon">
                        <FiMic size={14} />
                      </span>
                    ) : (
                      <span className="cc-notif__icon">
                        <FiCalendar size={14} />
                      </span>
                    )}
                    <div className="cc-notif__content">
                      <div className="cc-notif__title">{n.title}</div>
                      <div className="cc-notif__meta">{n.meta}</div>
                    </div>
                  </div>
                ))}

                {hasMoreNotifs && (
                  <button
                    className="cc-notif-more"
                    onClick={() => setShowAllNotifs((v) => !v)}
                    type="button"
                  >
                    <span>
                      {showAllNotifs
                        ? 'Show less'
                        : `+ More (${notifications.length - notifPreviewLimit})`}
                    </span>
                    {showAllNotifs ? (
                      <FiChevronUp size={16} />
                    ) : (
                      <FiChevronDown size={16} />
                    )}
                  </button>
                )}
              </div>
            </div>

            <GraduateTargeting
              onComplete={(data) =>
                setAdmissionsHome((prev) => ({
                  ...(prev || {}),
                  complete: data.complete,
                  targeting: data,
                  primary: data.targets?.[0]?.program || null,
                }))
              }
            />

            <div className="cc-section-divider"></div>

            {/* Level Section */}
            <div className="cc-level-section">
              <h3 className="cc-section-title">
                <FiTarget size={18} />
                Your Level
              </h3>

              {/* GRE Progress or Diagnostic Prompt */}
              {!ieltsOnlyUser &&
                (mandatoryTasks.greDiagnostic.completed ? (
                  <div className="cc-level-card cc-is-gre">
                    <div className="cc-level-header">
                      <span className="cc-level-label">GRE</span>
                      <span className="cc-level-score">
                        {mockData.gre.currentScore}/{mockData.gre.maxScore} →{' '}
                        {mockData.gre.targetScore}
                      </span>
                    </div>
                    <div className="cc-score-breakdown">
                      <div className="cc-score-item">
                        <span className="cc-score-label">V</span>
                        <span className="cc-score-value">
                          {mockData.gre.verbal}
                          <span className="cc-score-target">
                            → {mockData.gre.targetVerbal}
                          </span>
                        </span>
                      </div>
                      <div className="cc-score-item">
                        <span className="cc-score-label">Q</span>
                        <span className="cc-score-value">
                          {mockData.gre.quant}
                          <span className="cc-score-target">
                            → {mockData.gre.targetQuant}
                          </span>
                        </span>
                      </div>
                      <div className="cc-score-item">
                        <span className="cc-score-label">AWA</span>
                        <span className="cc-score-value">
                          {mockData.gre.awa}
                          <span className="cc-score-target">
                            → {mockData.gre.targetAwa}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="cc-plan-badge">
                      <span>Plan: {mockData.gre.planWeeks} weeks 🔥</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="cc-level-card cc-is-gre cc-is-pending"
                    onClick={() => navigate('/exams/gre/full/full-test-1')}
                  >
                    <div className="cc-level-header">
                      <span className="cc-level-label">GRE</span>
                      <span className="cc-level-pending">Test Required</span>
                    </div>
                    <div className="cc-pending-content">
                      <FiTarget size={24} className="cc-pending-icon" />
                      <p className="cc-pending-text">
                        Take your diagnostic test to discover your starting
                        level
                      </p>
                      <button className="cc-pending-cta">
                        Start Diagnostic Test
                        <FiArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}

              {/* IELTS Progress or Assessment Prompt */}
              {!greOnlyUser &&
                (mandatoryTasks.ieltsAssessment.completed ? (
                  <div className="cc-level-card cc-is-ielts">
                    <div className="cc-level-header">
                      <span className="cc-level-label">IELTS</span>
                      <span className="cc-level-score">
                        {mockData.ielts.currentLevel} →{' '}
                        {mockData.ielts.targetLevel}
                      </span>
                    </div>
                    <div className="cc-progress-bar-container">
                      <div
                        className="cc-progress-bar-fill"
                        style={{ width: `${mockData.ielts.progress}%` }}
                      ></div>
                    </div>
                    <div className="cc-ielts-breakdown">
                      <span>S: {mockData.ielts.speaking}</span>
                      <span>L: {mockData.ielts.listening}</span>
                      <span>R: {mockData.ielts.reading}</span>
                      <span>W: {mockData.ielts.writing}</span>
                    </div>
                    {user?.email && (
                      <EnglishRewardCard
                        journeyData={(() => {
                          try {
                            return JSON.parse(
                              localStorage.getItem(`languageJourney_${user.email}`) || 'null'
                            );
                          } catch {
                            return null;
                          }
                        })()}
                        eligibility={englishRewardEligibility}
                        onContinue={() =>
                          navigate('/exams/english/journey', {
                            state: { focusToday: true },
                          })
                        }
                      />
                    )}
                    {user?.email && (
                      <ToeflNextStepCard
                        compact
                        journey={readStoredLanguageJourney(user.email)}
                        currentLevel={mockData.ielts.currentLevel}
                        bootcamp={toeflBootcamp}
                      />
                    )}
                    {String(toeflBootcamp?.status || '').toLowerCase() === 'active' ? (
                      <button
                        type="button"
                        className="cc-pending-cta"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate('/exams/toefl/hub');
                        }}
                      >
                        Today’s TOEFL
                        <FiArrowRight size={14} />
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div
                    className="cc-level-card cc-is-ielts cc-is-pending"
                    onClick={() => navigate('/exams/english/speaking')}
                  >
                    <div className="cc-level-header">
                      <span className="cc-level-label">IELTS</span>
                      <span className="cc-level-pending">
                        Assessment Required
                      </span>
                    </div>
                    <div className="cc-pending-content">
                      <FiMic size={24} className="cc-pending-icon" />
                      <p className="cc-pending-text">
                        Take an assessment to evaluate your English level
                      </p>
                      <button className="cc-pending-cta">
                        Start Assessment
                        <FiArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
            </div>

            {/* Community Section */}
            <div className="cc-community-section">
              <h3 className="cc-section-title">
                <FiMessageCircle size={18} />
                Community
              </h3>
              <div className="cc-community-notifications">
                <div className="cc-community-highlight-list">
                  {displayedUpcomingEvent && (
                    <div className="cc-community-highlight cc-is-event">
                      <div className="cc-community-highlight-head">
                        <span className="cc-community-highlight-badge">
                          Community Event
                        </span>
                        <FiCalendar size={16} />
                      </div>
                      <strong className="cc-community-highlight-title">
                        {displayedUpcomingEvent.title}
                      </strong>
                      <span className="cc-community-highlight-meta">
                        {displayedUpcomingEvent.dateLabel}
                        {displayedUpcomingEvent.timeLabel
                          ? ` · ${displayedUpcomingEvent.timeLabel}`
                          : ''}
                      </span>
                      <div className="cc-community-highlight-actions">
                        <button
                          className="cc-community-highlight-action"
                          onClick={() =>
                            navigate('/community', {
                              state: { activeSection: 'events' },
                            })
                          }
                        >
                          Open Events <FiArrowRight size={14} />
                        </button>
                        <button
                          className="cc-community-highlight-action cc-is-secondary"
                          onClick={() =>
                            handleAddHighlightToCalendar(
                              displayedUpcomingEvent,
                              'event'
                            )
                          }
                        >
                          Add to calendar <FiCalendar size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {displayedUpcomingWebinar && (
                    <div className="cc-community-highlight cc-is-webinar">
                      <div className="cc-community-highlight-head">
                        <span className="cc-community-highlight-badge">
                          Upcoming Webinar
                        </span>
                        <FiVideo size={16} />
                      </div>
                      <strong className="cc-community-highlight-title">
                        {displayedUpcomingWebinar.title}
                      </strong>
                      <span className="cc-community-highlight-meta">
                        {displayedUpcomingWebinar.dateLabel}
                        {displayedUpcomingWebinar.timeLabel
                          ? ` · ${displayedUpcomingWebinar.timeLabel}`
                          : ''}
                      </span>
                      <div className="cc-community-highlight-actions">
                        <button
                          className="cc-community-highlight-action"
                          onClick={() =>
                            navigate(
                              displayedUpcomingWebinar?.slug
                                ? `/webinars/join/${displayedUpcomingWebinar.slug}`
                                : '/webinars'
                            )
                          }
                        >
                          Open Webinars <FiArrowRight size={14} />
                        </button>
                        <button
                          className="cc-community-highlight-action cc-is-secondary"
                          onClick={() =>
                            handleAddHighlightToCalendar(
                              displayedUpcomingWebinar,
                              'webinar'
                            )
                          }
                        >
                          Add to calendar <FiCalendar size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className="cc-community-stat"
                  onClick={() =>
                    navigate('/community', { state: { activeSection: 'general' } })
                  }
                >
                  <div className="cc-community-stat-copy">
                    <span className="cc-community-stat-value">
                      {mockData.community.newQuestions}
                    </span>
                    <span className="cc-community-stat-label">
                      {mockData.community.newQuestions === 1
                        ? 'new question'
                        : 'new questions'}
                    </span>
                  </div>
                  <span className="cc-community-shortcut">
                    Open Community <FiArrowRight size={14} />
                  </span>
                </button>

                <button
                  className="cc-community-stat"
                  onClick={() =>
                    navigate('/community', { state: { activeSection: 'general' } })
                  }
                >
                  <div className="cc-community-stat-copy">
                    <span className="cc-community-stat-value">
                      {mockData.community.newReplies}
                    </span>
                    <span className="cc-community-stat-label">
                      {mockData.community.newReplies === 1
                        ? 'reply to your posts'
                        : 'replies to your posts'}
                    </span>
                  </div>
                  <span className="cc-community-shortcut">
                    View Replies <FiArrowRight size={14} />
                  </span>
                </button>

                {!displayedUpcomingEvent &&
                  !displayedUpcomingWebinar &&
                  mockData.community.newQuestions === 0 &&
                  mockData.community.newReplies === 0 && (
                    <div className="cc-community-empty">
                      No new community updates yet.
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Right Column - Today's Tasks */}
          <div className="cc-right-column">
            <div className="cc-today-section">
              <h2 className="cc-today-title">
                <FiBookOpen size={22} />
                TODAY
              </h2>
              {programWindowDays.length > 0 && (
                <ProgramTimeline
                  days={programWindowDays}
                  selectedOffset={selectedProgramOffset}
                  onSelect={(offset) => {
                    setSelectedProgramOffset(offset);
                    sessionStorage.setItem('home_program_offset', String(offset));
                    sessionStorage.setItem('gre_program_offset', String(offset));
                    setDailyArticles(
                      articlesFromProgramWindow({ days: greWindowDays }, offset)
                    );
                  }}
                />
              )}

              {/* GRE Tasks */}
              {mockData.todayTasks.gre.length > 0 && (
                <>
                  <div className="cc-task-group">
                    <h4 className="cc-task-group-title">
                      <FiBook size={16} />
                      GRE
                    </h4>
                    <div className="cc-task-list">
                      {mockData.todayTasks.gre.map((task) => (
                        <div
                          key={task.id}
                          className={getTaskItemClassName(taskStatuses[task.id])}
                          onClick={() => handleTaskClick(task)}
                        >
                          <span className="cc-task-status" aria-hidden="true">
                            {taskStatuses[task.id] === 'completed' ? (
                              <FiCheck size={14} />
                            ) : taskStatuses[task.id] === 'in_progress' ? (
                              <FiMinus size={14} />
                            ) : null}
                          </span>
                          <div className="cc-task-content">
                            <span className="cc-task-label">{task.label}</span>
                            {task.sublabel && (
                              <span className="cc-task-sublabel">
                                {task.sublabel}
                              </span>
                            )}
                          </div>
                          <FiArrowRight className="cc-task-arrow" size={14} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="cc-task-divider"></div>
                </>
              )}

              {/* IELTS / TOEFL Tasks */}
              {mockData.todayTasks.ielts.length > 0 && (
                <div className="cc-task-group">
                  <h4 className="cc-task-group-title">
                    <FiMic size={16} />
                    {String(toeflBootcamp?.status || '').toLowerCase() === 'active'
                      ? 'TOEFL'
                      : 'IELTS'}
                  </h4>
                  <p className="cc-task-group-hint">
                    {String(toeflBootcamp?.status || '').toLowerCase() === 'active'
                      ? 'Lesson then practice — mocks on scheduled days'
                      : 'Reading, Listening, Speaking and Writing'}
                  </p>
                  <div className="cc-task-list">
                    {mockData.todayTasks.ielts.map((task) => (
                      <div
                        key={task.id}
                        className={getTaskItemClassName(taskStatuses[task.id])}
                        onClick={() => handleTaskClick(task)}
                      >
                        <span className="cc-task-status" aria-hidden="true">
                          {taskStatuses[task.id] === 'completed' ? (
                            <FiCheck size={14} />
                          ) : taskStatuses[task.id] === 'in_progress' ? (
                            <FiMinus size={14} />
                          ) : null}
                        </span>
                        <div className="cc-task-content">
                          <span className="cc-task-label">{task.label}</span>
                          {task.sublabel && (
                            <span className="cc-task-sublabel">
                              {task.sublabel}
                            </span>
                          )}
                        </div>
                        <FiArrowRight className="cc-task-arrow" size={14} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mockData.todayTasks.ielts.length > 0 && (
                <div className="cc-task-divider"></div>
              )}

              {/* Priority Tasks */}
              {mockData.todayTasks.priority.length > 0 && (
                <>
                  <div className="cc-task-group cc-is-priority">
                    <h4 className="cc-task-group-title">
                      <FiAlertCircle size={16} />
                      PRIORITY
                    </h4>
                    <div className="cc-task-list">
                      {mockData.todayTasks.priority.map((task) => (
                        <div
                          key={task.id}
                          className={getTaskItemClassName(
                            taskStatuses[task.id],
                            'cc-is-priority'
                          )}
                          onClick={() => handleTaskClick(task)}
                        >
                          <span className="cc-task-status" aria-hidden="true">
                            {taskStatuses[task.id] === 'completed' ? (
                              <FiCheck size={14} />
                            ) : taskStatuses[task.id] === 'in_progress' ? (
                              <FiMinus size={14} />
                            ) : null}
                          </span>
                          <div className="cc-task-content">
                            <span className="cc-task-label">{task.label}</span>
                            {task.subtext && (
                              <span className="cc-task-subtext">
                                {task.subtext}
                              </span>
                            )}
                          </div>
                          <FiArrowRight className="cc-task-arrow" size={14} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cc-task-divider"></div>
                </>
              )}

              {/* More Tasks Toggle */}
              <button
                className="cc-more-tasks-toggle"
                onClick={() => setShowMoreTasks(!showMoreTasks)}
              >
                <span>{showMoreTasks ? '− Less' : '+ More'}</span>
                {!showMoreTasks && (
                  <span className="cc-more-count">{moreTasksCount}</span>
                )}
                {showMoreTasks ? (
                  <FiChevronUp size={16} />
                ) : (
                  <FiChevronDown size={16} />
                )}
              </button>

              {/* Expanded More Tasks */}
              {showMoreTasks && (
                <div className="cc-more-tasks-expanded">
                  {/* MBA App Tasks */}
                  {mockData.moreTasks.mbaApp.length > 0 && (
                  <div className="cc-task-group">
                    <h4 className="cc-task-group-title">
                      <FiFileText size={16} />
                      APPLICATIONS
                    </h4>
                    <div className="cc-task-list">
                      {mockData.moreTasks.mbaApp.map((task) => (
                        <div
                          key={task.id}
                          className={getTaskItemClassName(taskStatuses[task.id])}
                          onClick={() => handleTaskClick(task)}
                        >
                          <span className="cc-task-status" aria-hidden="true">
                            {taskStatuses[task.id] === 'completed' ? (
                              <FiCheck size={14} />
                            ) : taskStatuses[task.id] === 'in_progress' ? (
                              <FiMinus size={14} />
                            ) : null}
                          </span>
                          <span className="cc-task-label">{task.label}</span>
                          <FiArrowRight className="cc-task-arrow" size={14} />
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Weekend Tasks - only show on weekdays */}
                  {mockData.moreTasks.weekend.length > 0 && (
                    <>
                      <div className="cc-task-divider"></div>
                      <div className="cc-task-group">
                        <h4 className="cc-task-group-title">
                          <FiTarget size={16} />
                          WEEKEND PREVIEW
                        </h4>
                        <div className="cc-task-list">
                          {mockData.moreTasks.weekend.map((task) => (
                            <div
                              key={task.id}
                              className={getTaskItemClassName(
                                taskStatuses[task.id]
                              )}
                              onClick={() => handleTaskClick(task)}
                            >
                              <span className="cc-task-status" aria-hidden="true">
                                {taskStatuses[task.id] === 'completed' ? (
                                  <FiCheck size={14} />
                                ) : taskStatuses[task.id] === 'in_progress' ? (
                                  <FiMinus size={14} />
                                ) : null}
                              </span>
                              <span className="cc-task-label">
                                {task.label}
                              </span>
                              <FiArrowRight
                                className="cc-task-arrow"
                                size={14}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Training Plan Section */}
        <section className="cc-training-plan">
          <h2 className="cc-training-plan__title">
            <FiClipboard size={22} />
            Your Training Plan
          </h2>

          <div className="cc-training-plan__cards">
            {!ieltsOnlyUser && !mandatoryTasks.greDiagnostic.completed ? (
              <div
                className="cc-level-card cc-is-gre cc-is-pending"
                onClick={() => navigate('/exams/gre/full/full-test-1')}
              >
                <div className="cc-level-header">
                  <span className="cc-level-label">GRE Training Plan</span>
                  <span className="cc-level-pending">Test Required</span>
                </div>
                <div className="cc-pending-content">
                  <FiTarget size={24} className="cc-pending-icon" />
                  <p className="cc-pending-text">
                    Complete GRE Test 1 first to unlock your personalized
                    training plan.
                  </p>
                  <button className="cc-pending-cta">
                    Start GRE Test 1
                    <FiArrowRight size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <>
            {/* Daily Routine */}
            <div className="cc-plan-card cc-plan-card--daily">
              <div className="cc-plan-card__header">
                <div className="cc-plan-card__icon">
                  <FiSunrise size={20} />
                </div>
                <div className="cc-plan-card__title-group">
                  <h3 className="cc-plan-card__title">Daily Routine</h3>
                  <span className="cc-plan-card__duration">
                    {mockData.trainingPlan.daily?.estimatedTime || '~30 min'}
                  </span>
                </div>
              </div>
              <div className="cc-plan-card__tasks">
                {mockData.trainingPlan.daily.verbal.map((task) => (
                  <div
                    key={task.id}
                    className="cc-plan-task"
                    onClick={() => handleTaskClick(task)}
                  >
                    <div className="cc-plan-task__info">
                      <span className="cc-plan-task__label">{task.label}</span>
                      <span className="cc-plan-task__desc">
                        {task.description}
                      </span>
                    </div>
                    <FiArrowRight className="cc-plan-task__arrow" size={16} />
                  </div>
                ))}
              </div>
            </div>

            {/* Weekend Activities */}
            <div className="cc-plan-card cc-plan-card--weekend">
              <div className="cc-plan-card__header">
                <div className="cc-plan-card__icon">
                  <FiSun size={20} />
                </div>
                <div className="cc-plan-card__title-group">
                  <h3 className="cc-plan-card__title">Weekend Activities</h3>
                  <span className="cc-plan-card__duration">
                    Split across Sat & Sun
                  </span>
                </div>
              </div>

              {/* Saturday */}
              <div className="cc-plan-card__day-section">
                <div className="cc-plan-card__day-header">
                  <span className="cc-plan-card__day-label">SATURDAY</span>
                  <span className="cc-plan-card__day-time">
                    {mockData.trainingPlan.weekend?.saturday?.estimatedTime ||
                      '~2h'}
                  </span>
                </div>

                <div className="cc-plan-card__section">
                  <span className="cc-plan-card__section-label">VERBAL</span>
                  <div className="cc-plan-card__tasks">
                    {(
                      mockData.trainingPlan.weekend?.saturday?.verbal || []
                    ).map((task) => (
                      <div
                        key={task.id}
                        className="cc-plan-task"
                        onClick={() => handleTaskClick(task)}
                      >
                        <div className="cc-plan-task__info">
                          <span className="cc-plan-task__label">
                            {task.label}
                          </span>
                          <span className="cc-plan-task__desc">
                            {task.description}
                          </span>
                        </div>
                        <FiArrowRight
                          className="cc-plan-task__arrow"
                          size={16}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="cc-plan-card__section">
                  <span className="cc-plan-card__section-label">QUANT</span>
                  <div className="cc-plan-card__tasks">
                    {(mockData.trainingPlan.weekend?.saturday?.quant || []).map(
                      (task) => (
                        <div
                          key={task.id}
                          className="cc-plan-task"
                          onClick={() => handleTaskClick(task)}
                        >
                          <div className="cc-plan-task__info">
                            <span className="cc-plan-task__label">
                              {task.label}
                            </span>
                            <span className="cc-plan-task__desc">
                              {task.description}
                            </span>
                          </div>
                          <FiArrowRight
                            className="cc-plan-task__arrow"
                            size={16}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Sunday */}
              <div className="cc-plan-card__day-section">
                <div className="cc-plan-card__day-header">
                  <span className="cc-plan-card__day-label">SUNDAY</span>
                  <span className="cc-plan-card__day-time">
                    {mockData.trainingPlan.weekend?.sunday?.estimatedTime ||
                      '~1h45'}
                  </span>
                </div>

                <div className="cc-plan-card__section">
                  <span className="cc-plan-card__section-label">VERBAL</span>
                  <div className="cc-plan-card__tasks">
                    {(mockData.trainingPlan.weekend?.sunday?.verbal || []).map(
                      (task) => (
                        <div
                          key={task.id}
                          className="cc-plan-task"
                          onClick={() => handleTaskClick(task)}
                        >
                          <div className="cc-plan-task__info">
                            <span className="cc-plan-task__label">
                              {task.label}
                            </span>
                            <span className="cc-plan-task__desc">
                              {task.description}
                            </span>
                          </div>
                          <FiArrowRight
                            className="cc-plan-task__arrow"
                            size={16}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="cc-plan-card__section">
                  <span className="cc-plan-card__section-label">QUANT</span>
                  <div className="cc-plan-card__tasks">
                    {(mockData.trainingPlan.weekend?.sunday?.quant || []).map(
                      (task) => (
                        <div
                          key={task.id}
                          className="cc-plan-task"
                          onClick={() => handleTaskClick(task)}
                        >
                          <div className="cc-plan-task__info">
                            <span className="cc-plan-task__label">
                              {task.label}
                            </span>
                            <span className="cc-plan-task__desc">
                              {task.description}
                            </span>
                          </div>
                          <FiArrowRight
                            className="cc-plan-task__arrow"
                            size={16}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Note about daily tasks */}
              {mockData.trainingPlan.weekend?.note && (
                <div className="cc-plan-card__note">
                  <span>{mockData.trainingPlan.weekend.note}</span>
                </div>
              )}
            </div>

            {/* Biweekly Mock Test */}
            <div className="cc-plan-card cc-plan-card--biweekly">
              <div className="cc-plan-card__header">
                <div className="cc-plan-card__icon">
                  <FiTarget size={20} />
                </div>
                <div className="cc-plan-card__title-group">
                  <h3 className="cc-plan-card__title">GRE Mock Tests</h3>
                  <span className="cc-plan-card__subtitle">
                    {mockData.trainingPlan.biweekly.completedTests}/
                    {mockData.trainingPlan.biweekly.totalTests} completed
                  </span>
                </div>
              </div>

              {/* Past Tests */}
              {mockData.trainingPlan.biweekly.pastTests?.length > 0 && (
                <div className="cc-plan-card__tests-section">
                  <span className="cc-plan-card__section-label">COMPLETED</span>
                  <div className="cc-plan-card__tests-list">
                    {mockData.trainingPlan.biweekly.pastTests.map((test) => (
                      <div
                        key={test.id}
                        className="cc-plan-test cc-plan-test--completed"
                      >
                        <div className="cc-plan-test__info">
                          <FiCheck className="cc-plan-test__check" size={14} />
                          <span className="cc-plan-test__label">
                            {test.label}
                          </span>
                        </div>
                        <div className="cc-plan-test__actions">
                          <button
                            className="cc-plan-test__btn cc-plan-test__btn--review"
                            onClick={() => navigate(test.reviewPath)}
                          >
                            Review
                          </button>
                          <button
                            className="cc-plan-test__btn cc-plan-test__btn--retake"
                            onClick={() => navigate(test.path)}
                          >
                            Retake
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Test */}
              {mockData.trainingPlan.biweekly.currentTest && (
                <div className="cc-plan-card__tests-section">
                  <span className="cc-plan-card__section-label">NEXT UP</span>
                  <div
                    className={`cc-plan-card__countdown ${
                      mockData.trainingPlan.biweekly.currentTest.isOverdue > 0
                        ? 'cc-plan-card__countdown--overdue'
                        : ''
                    }`}
                  >
                    <div className="cc-plan-card__next-date">
                      <FiCalendar size={14} />
                      <span>
                        {mockData.trainingPlan.biweekly.currentTest.isOverdue >
                        0
                          ? formatFullDate(
                              mockData.trainingPlan.biweekly.currentTest
                                .targetDate
                            )
                          : formatFullDate(
                              mockData.trainingPlan.biweekly.currentTest
                                .nextTestDate
                            )}
                      </span>
                    </div>
                    <div
                      className={`cc-plan-card__days-left ${
                        mockData.trainingPlan.biweekly.currentTest.isOverdue > 0
                          ? 'cc-plan-card__days-left--overdue'
                          : ''
                      }`}
                    >
                      {mockData.trainingPlan.biweekly.currentTest.isOverdue > 0
                        ? `Overdue by ${mockData.trainingPlan.biweekly.currentTest.isOverdue} days`
                        : getDaysUntil(
                              mockData.trainingPlan.biweekly.currentTest
                                .nextTestDate
                            ) <= 0
                          ? 'Today!'
                          : `In ${getDaysUntil(
                              mockData.trainingPlan.biweekly.currentTest
                                .nextTestDate
                            )} days`}
                    </div>
                  </div>

                  <div className="cc-plan-card__progress">
                    <div
                      className="cc-plan-card__progress-fill"
                      style={{ width: `${getBiweeklyProgress()}%` }}
                    />
                  </div>

                  <div
                    className="cc-plan-task cc-plan-task--main"
                    onClick={() =>
                      navigate(mockData.trainingPlan.biweekly.currentTest.path)
                    }
                  >
                    <div className="cc-plan-task__info">
                      <span className="cc-plan-task__label">
                        {mockData.trainingPlan.biweekly.currentTest.label}
                      </span>
                      <span className="cc-plan-task__desc">
                        {mockData.trainingPlan.biweekly.description} • Duration:{' '}
                        {mockData.trainingPlan.biweekly.duration}
                      </span>
                    </div>
                    <FiArrowRight className="cc-plan-task__arrow" size={16} />
                  </div>
                </div>
              )}

              {/* All tests done message */}
              {mockData.trainingPlan.biweekly.allTestsDone && (
                <div className="cc-plan-card__all-done">
                  <FiCheck size={18} />
                  <span>All mock tests completed! 🎉</span>
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
